import React, { useState } from 'react';
import { createPatientLink } from '../lib/patientLinks';
import { Activity, Brain, Heart, FileText, CheckCircle, Calculator, ChevronRight, AlertCircle, Copy, Check, Globe, Home, BookOpen, Info, RefreshCw, ExternalLink } from 'lucide-react';
import EasiGuideModal from './EasiGuideModal';
import PasiGuideModal from './PasiGuideModal';
import PasiInfoModal from './PasiInfoModal';
import CalculatorGuideModal from './CalculatorGuideModal';

// === DATA STRUCTURES FOR QUESTIONNAIRES === //

const BASDAI_QUESTIONS = {
  en: [
    "How would you describe the overall level of fatigue/tiredness you have experienced?",
    "How would you describe the overall level of AS neck, back or hip pain you have had?",
    "How would you describe the overall level of pain/swelling in joints other than neck, back, hips?",
    "How would you describe the overall level of discomfort you have had from any areas tender to touch or pressure?",
    "How would you describe the overall level of morning stiffness you have had from the time you wake up?",
    "How long does your morning stiffness last from the time you wake up?"
  ],
  ar: [
    "كيف تصف مستوى التعب والإرهاق العام الذي عانيت منه؟",
    "كيف تصف مستوى ألم الرقبة أو الظهر أو الورك المرتبط بالتهاب الفقار؟",
    "كيف تصف مستوى الألم أو التورم في المفاصل الأخرى (غير الرقبة والظهر والورك)؟",
    "كيف تصف مستوى الانزعاج من أي مناطق حساسة للمس أو الضغط؟",
    "كيف تصف مستوى التيبس الصباحي الذي تعاني منه عند الاستيقاظ؟",
    "ما هي مدة استمرار التيبس الصباحي منذ لحظة استيقاظك؟"
  ]
};

const MDQ_QUESTIONS = {
  en: [
    "Felt so good or hyper that other people thought you were not your normal self or were so hyper that you got into trouble?",
    "Were so irritable that you shouted at people or started fights or arguments?",
    "Felt much more self-confident than usual?",
    "Got much less sleep than usual and found you didn't really miss it?",
    "Were much more talkative or spoke much faster than usual?",
    "Thoughts raced through your head or you couldn't slow your mind down?",
    "Were so easily distracted by things around you that you had trouble concentrating or staying on track?",
    "Had much more energy than usual?",
    "Were much more active or did many more things than usual?",
    "Were much more social or outgoing than usual, for example, you telephoned friends in the middle of the night?",
    "Were much more interested in sex than usual?",
    "Did things that were unusual for you or that other people might have thought were excessive, foolish, or risky?",
    "Spent money got you or your family into trouble?"
  ],
  ar: [
    "شعرت بأنك بحالة جيدة جداً أو مفرط النشاط لدرجة أن الآخرين لاحظوا أنك لست على طبيعتك، أو أوقعك ذلك في مشاكل؟",
    "كنت سريع الانفعال لدرجة الصراخ أو افتعال شجارات ونقاشات حادة؟",
    "شعرت بثقة بالنفس أكثر بكثير من المعتاد؟",
    "نمت أقل بكثير من المعتاد ولم تشعر بالحاجة للنوم؟",
    "كنت تتحدث أكثر بكثير أو أسرع بكثير من المعتاد؟",
    "تسارعت الأفكار في رأسك ولم تستطع إبطاء تفكيرك؟",
    "كنت تتشتت بسهولة بالأشياء من حولك وواجهت صعوبة في التركيز؟",
    "كان لديك طاقة أكبر بكثير من المعتاد؟",
    "كنت أكثر نشاطاً أو قمت بأشياء أكثر بكثير من المعتاد؟",
    "كنت اجتماعياً أو منفتحاً أكثر بكثير من المعتاد (مثلاً الاتصال بالأصدقاء في منتصف الليل)؟",
    "كنت مهتماً بالجنس أكثر بكثير من المعتاد؟",
    "قمت بأشياء غير معتادة أو قد يراها الآخرون مبالغاً فيها أو غبية أو محفوفة بالمخاطر؟",
    "أنفقت أموالاً أوقعتك أو أوقعت عائلتك في مشاكل؟"
  ]
};

const POEM_QUESTIONS = {
  en: [
    "How many days has your skin been itchy because of eczema?",
    "How many nights has your sleep been disturbed because of eczema?",
    "How many days has your skin been bleeding because of eczema?",
    "How many days has your skin been weeping or oozing clear fluid because of eczema?",
    "How many days has your skin been cracked because of eczema?",
    "How many days has your skin been flaking off because of eczema?",
    "How many days has your skin felt dry or rough because of eczema?"
  ],
  ar: [
    "كم يوماً شعرت بحكة في جلدك بسبب الإكزيما؟",
    "كم ليلة اضطرب نومك بسبب الإكزيما؟",
    "كم يوماً عانى جلدك من النزيف بسبب الإكزيما؟",
    "كم يوماً عانى جلدك من الإفرازات أو تقرحات سائلة شفافة بسبب الإكزيما؟",
    "كم يوماً عانى جلدك من التشققات بسبب الإكزيما؟",
    "كم يوماً كان جلدك يتقشر ويتساقط بسبب الإكزيما؟",
    "كم يوماً شعرت بأن جلدك جاف أو خشن بسبب الإكزيما؟"
  ]
};

const POEM_OPTIONS = {
  en: [
    { label: "No days", value: 0 },
    { label: "1-2 days", value: 1 },
    { label: "3-4 days", value: 2 },
    { label: "5-6 days", value: 3 },
    { label: "Every day", value: 4 }
  ],
  ar: [
    { label: "ولا يوم", value: 0 },
    { label: "1-2 أيام", value: 1 },
    { label: "3-4 أيام", value: 2 },
    { label: "5-6 أيام", value: 3 },
    { label: "كل يوم", value: 4 }
  ]
};

const DLQI_QUESTIONS = {
  en: [
    "Over the last week, how itchy, sore, painful or stinging has your skin been?",
    "Over the last week, how embarrassed or self conscious have you been because of your skin?",
    "Over the last week, how much has your skin interfered with you going shopping or looking after your home or garden?",
    "Over the last week, how much has your skin influenced the clothes you wear?",
    "Over the last week, how much has your skin affected any social or leisure activities?",
    "Over the last week, how much has your skin made it difficult for you to do any sport?",
    "Over the last week, has your skin prevented you from working or studying?",
    "Over the last week, how much has your skin created problems with your partner or any of your close friends or relatives?",
    "Over the last week, how much has your skin caused any sexual difficulties?",
    "Over the last week, how much of a problem has the treatment for your skin been, for example by making your home messy, or by taking up time?"
  ],
  ar: [
    "خلال الأسبوع الماضي، ما مدى الحكة أو الألم أو الوخز الذي عانيت منه في جلدك؟",
    "خلال الأسبوع الماضي، ما مدى شعورك بالإحراج أو الخجل بسبب جلدك؟",
    "خلال الأسبوع الماضي، إلى أي مدى أثر جلدك على تسوقك أو اهتمامك بمنزلك أو حديقتك؟",
    "خلال الأسبوع الماضي، إلى أي مدى أثر جلدك على نوع الملابس التي ترتديها؟",
    "خلال الأسبوع الماضي، إلى أي مدى أثر جلدك على أي أنشطة اجتماعية أو ترفيهية؟",
    "خلال الأسبوع الماضي، إلى أي مدى صعّب جلدك ممارستك لأي رياضة؟",
    "خلال الأسبوع الماضي، هل منعك جلدك من العمل أو الدراسة؟",
    "خلال الأسبوع الماضي، إلى أي مدى تسبب جلدك في مشاكل مع شريك حياتك أو أصدقائك أو أقاربك؟",
    "خلال الأسبوع الماضي، إلى أي مدى تسبب جلدك في أي صعوبات جنسية؟",
    "خلال الأسبوع الماضي، إلى أي مدى كان علاج جلدك يمثل مشكلة، على سبيل المثال بتوسيخ منزلك أو استهلاك وقتك؟"
  ]
};

const DLQI_OPTIONS = {
  en: [
    { label: 'Very much', value: 3 },
    { label: 'A lot', value: 2 },
    { label: 'A little', value: 1 },
    { label: 'Not at all', value: 0 },
  ],
  ar: [
    { label: 'كثيراً جداً', value: 3 },
    { label: 'كثيراً', value: 2 },
    { label: 'قليلاً', value: 1 },
    { label: 'لا إطلاقاً', value: 0 },
  ]
};



const HADS_QUESTIONS = {
  en: [
    { text: "1. I feel tense or 'wound up':", type: "A", options: [{label: "Most of the time", val: 3}, {label: "A lot of the time", val: 2}, {label: "From time to time, occasionally", val: 1}, {label: "Not at all", val: 0}] },
    { text: "2. I still enjoy the things I used to enjoy:", type: "D", options: [{label: "Definitely as much", val: 0}, {label: "Not quite so much", val: 1}, {label: "Only a little", val: 2}, {label: "Hardly at all", val: 3}] },
    { text: "3. I get a sort of frightened feeling as if something awful is about to happen:", type: "A", options: [{label: "Very definitely and quite badly", val: 3}, {label: "Yes, but not too badly", val: 2}, {label: "A little, but it doesn't worry me", val: 1}, {label: "Not at all", val: 0}] },
    { text: "4. I can laugh and see the funny side of things:", type: "D", options: [{label: "As much as I always could", val: 0}, {label: "Not quite so much now", val: 1}, {label: "Definitely not so much now", val: 2}, {label: "Not at all", val: 3}] },
    { text: "5. Worrying thoughts go through my mind:", type: "A", options: [{label: "A great deal of the time", val: 3}, {label: "A lot of the time", val: 2}, {label: "From time to time, but not too often", val: 1}, {label: "Only occasionally", val: 0}] },
    { text: "6. I feel cheerful:", type: "D", options: [{label: "Not at all", val: 3}, {label: "Not often", val: 2}, {label: "Sometimes", val: 1}, {label: "Most of the time", val: 0}] },
    { text: "7. I can sit at ease and feel relaxed:", type: "A", options: [{label: "Definitely", val: 0}, {label: "Usually", val: 1}, {label: "Not often", val: 2}, {label: "Not at all", val: 3}] },
    { text: "8. I feel as if I am slowed down:", type: "D", options: [{label: "Nearly all the time", val: 3}, {label: "Very often", val: 2}, {label: "Sometimes", val: 1}, {label: "Not at all", val: 0}] },
    { text: "9. I get a sort of frightened feeling like 'butterflies' in the stomach:", type: "A", options: [{label: "Not at all", val: 0}, {label: "Occasionally", val: 1}, {label: "Quite often", val: 2}, {label: "Very often", val: 3}] },
    { text: "10. I have lost interest in my appearance:", type: "D", options: [{label: "Definitely", val: 3}, {label: "I don't take as much care as I should", val: 2}, {label: "I may not take quite as much care", val: 1}, {label: "I take just as much care as ever", val: 0}] },
    { text: "11. I feel restless as if I have to be on the move:", type: "A", options: [{label: "Very much indeed", val: 3}, {label: "Quite a lot", val: 2}, {label: "Not very much", val: 1}, {label: "Not at all", val: 0}] },
    { text: "12. I look forward with enjoyment to things:", type: "D", options: [{label: "As much as I ever did", val: 0}, {label: "Rather less than I used to", val: 1}, {label: "Definitely less than I used to", val: 2}, {label: "Hardly at all", val: 3}] },
    { text: "13. I get sudden feelings of panic:", type: "A", options: [{label: "Very often indeed", val: 3}, {label: "Quite often", val: 2}, {label: "Not very often", val: 1}, {label: "Not at all", val: 0}] },
    { text: "14. I can enjoy a good book or radio or TV program:", type: "D", options: [{label: "Often", val: 0}, {label: "Sometimes", val: 1}, {label: "Not often", val: 2}, {label: "Very seldom", val: 3}] }
  ],
  ar: [
    { text: "1. أشعر بالتوتر أو الانفعال:", type: "A", options: [{label: "معظم الوقت", val: 3}, {label: "كثيراً من الوقت", val: 2}, {label: "من حين لآخر", val: 1}, {label: "لا أشعر بذلك إطلاقاً", val: 0}] },
    { text: "2. ما زلت أستمتع بالأشياء التي كنت أستمتع بها عادة:", type: "D", options: [{label: "بالتأكيد بنفس المقدار", val: 0}, {label: "ليس بنفس المقدار تماماً", val: 1}, {label: "قليلاً فقط", val: 2}, {label: "بالكاد على الإطلاق", val: 3}] },
    { text: "3. ينتابني شعور بالخوف وكأن شيئاً مروعاً سيحدث:", type: "A", options: [{label: "بشكل مؤكد وسيء جداً", val: 3}, {label: "نعم، ولكن ليس بشكل سيء", val: 2}, {label: "قليلاً، ولا يقلقني", val: 1}, {label: "لا أشعر بذلك إطلاقاً", val: 0}] },
    { text: "4. أستطيع أن أضحك وأرى الجانب المضحك للأشياء:", type: "D", options: [{label: "بنفس القدر الذي كنت أستطيعه دائماً", val: 0}, {label: "ليس بقدر ما كنت في السابق", val: 1}, {label: "بالتأكيد ليس بقدر ما كنت في السابق", val: 2}, {label: "لا أستطيع إطلاقاً", val: 3}] },
    { text: "5. تساورني أفكار مقلقة:", type: "A", options: [{label: "جزءاً كبيراً من الوقت", val: 3}, {label: "كثيراً من الوقت", val: 2}, {label: "من حين لآخر، ولكن ليس كثيراً", val: 1}, {label: "فقط في بعض الأحيان", val: 0}] },
    { text: "6. أشعر بالبهجة:", type: "D", options: [{label: "لا أشعر بها إطلاقاً", val: 3}, {label: "ليس غالباً", val: 2}, {label: "في بعض الأحيان", val: 1}, {label: "معظم الوقت", val: 0}] },
    { text: "7. أستطيع الجلوس براحة وأشعر بالاسترخاء:", type: "A", options: [{label: "بالتأكيد", val: 0}, {label: "عادةً", val: 1}, {label: "ليس غالباً", val: 2}, {label: "لا أستطيع إطلاقاً", val: 3}] },
    { text: "8. أشعر وكأن حركتي بطيئة:", type: "D", options: [{label: "تقريباً كل الوقت", val: 3}, {label: "غالباً جداً", val: 2}, {label: "في بعض الأحيان", val: 1}, {label: "لا أشعر بذلك إطلاقاً", val: 0}] },
    { text: "9. ينتابني شعور بالخوف (فراشات في المعدة):", type: "A", options: [{label: "لا أشعر بذلك إطلاقاً", val: 0}, {label: "في بعض الأحيان", val: 1}, {label: "غالباً", val: 2}, {label: "غالباً جداً", val: 3}] },
    { text: "10. فقدت الاهتمام بمظهري:", type: "D", options: [{label: "بالتأكيد", val: 3}, {label: "لا أهتم بنفس القدر الذي ينبغي", val: 2}, {label: "قد لا أهتم بنفس القدر", val: 1}, {label: "أهتم بنفس القدر كالمعتاد", val: 0}] },
    { text: "11. أشعر بعدم الاستقرار وكأنني يجب أن أتحرك:", type: "A", options: [{label: "كثيراً جداً", val: 3}, {label: "إلى حد كبير", val: 2}, {label: "ليس كثيراً", val: 1}, {label: "لا أشعر بذلك إطلاقاً", val: 0}] },
    { text: "12. أتطلع باستمتاع للأشياء:", type: "D", options: [{label: "بنفس القدر الذي عهدته", val: 0}, {label: "أقل مما اعتدت عليه", val: 1}, {label: "أقل بالتأكيد مما اعتدت عليه", val: 2}, {label: "بالكاد على الإطلاق", val: 3}] },
    { text: "13. تنتابني مشاعر ذعر مفاجئة:", type: "A", options: [{label: "غالباً جداً", val: 3}, {label: "غالباً", val: 2}, {label: "ليس غالباً", val: 1}, {label: "لا تنتابني إطلاقاً", val: 0}] },
    { text: "14. أستطيع الاستمتاع بكتاب جيد أو برنامج تلفزيوني/إذاعي:", type: "D", options: [{label: "غالباً", val: 0}, {label: "أحياناً", val: 1}, {label: "ليس غالباً", val: 2}, {label: "نادراً جداً", val: 3}] }
  ]
};

const PHQ9_QUESTIONS = {
  en: [
    "Little interest or pleasure in doing things",
    "Feeling down, depressed, or hopeless",
    "Trouble falling or staying asleep, or sleeping too much",
    "Feeling tired or having little energy",
    "Poor appetite or overeating",
    "Feeling bad about yourself — or that you are a failure or have let yourself or your family down",
    "Trouble concentrating on things, such as reading the newspaper or watching television",
    "Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual",
    "Thoughts that you would be better off dead or of hurting yourself in some way"
  ],
  ar: [
    "قلة الاهتمام أو المتعة في القيام بالأشياء",
    "الشعور بالإحباط أو الاكتئاب أو اليأس",
    "صعوبة في النوم أو البقاء نائماً، أو النوم أكثر من اللازم",
    "الشعور بالتعب أو قلة الطاقة",
    "ضعف الشهية أو الإفراط في الأكل",
    "الشعور بالسوء تجاه نفسك — أو أنك فاشل أو خذلت نفسك أو عائلتك",
    "صعوبة في التركيز على الأشياء، مثل قراءة الصحيفة أو مشاهدة التلفاز",
    "التحرك أو التحدث ببطء شديد بحيث يمكن للآخرين ملاحظة ذلك؟ أو العكس — الشعور بالتململ لدرجة أنك تتحرك أكثر من المعتاد",
    "أفكار بأنه من الأفضل لو كنت ميتاً، أو التفكير في إيذاء نفسك بأي شكل"
  ]
};

const PHQ9_OPTIONS = {
  en: [
    { label: 'Not at all', value: 0 },
    { label: 'Several days', value: 1 },
    { label: 'More than half the days', value: 2 },
    { label: 'Nearly every day', value: 3 },
  ],
  ar: [
    { label: 'لا إطلاقاً', value: 0 },
    { label: 'عدة أيام', value: 1 },
    { label: 'أكثر من نصف الأيام', value: 2 },
    { label: 'تقريباً كل يوم', value: 3 },
  ]
};

const PEST_QUESTIONS = {
  en: [
    "Have you ever had a swollen joint (or joints)?",
    "Has a doctor ever told you that you had arthritis?",
    "Do your fingernails or toenails have holes or pits?",
    "Have you had pain in your heel?",
    "Have you had a finger or toe that was completely swollen and painful for no apparent reason?"
  ],
  ar: [
    "هل عانيت يوماً من تورم في مفصل (أو مفاصل)؟",
    "هل أخبرك طبيب يوماً أنك مصاب بالتهاب المفاصل؟",
    "هل توجد ثقوب أو تنقرات في أظافر يديك أو قدميك؟",
    "هل عانيت من ألم في كعب قدمك؟",
    "هل حدث أن تورم إصبع يدك أو قدمك بالكامل وأصبح مؤلماً لسبب غير واضح؟"
  ]
};

const SCORTEN_PARAMS = [
  { label: "Age > 40 years", value: 1 },
  { label: "Heart rate > 120 bpm", value: 1 },
  { label: "Cancer / Malignancy", value: 1 },
  { label: "BSA detached on day 1 > 10%", value: 1 },
  { label: "Serum BUN > 28 mg/dL (10 mmol/L)", value: 1 },
  { label: "Serum glucose > 252 mg/dL (14 mmol/L)", value: 1 },
  { label: "Serum bicarbonate < 20 mEq/L", value: 1 },
];

const SLICC_CLINICAL = [
  "Acute cutaneous lupus",
  "Chronic cutaneous lupus",
  "Oral or nasal ulcers",
  "Non-scarring alopecia",
  "Arthritis (2+ joints)",
  "Serositis (Pleurisy or Pericarditis)",
  "Renal (Urine protein > 0.5g/24h or red cell casts)",
  "Neurologic (Seizures, psychosis, etc.)",
  "Hemolytic anemia",
  "Leukopenia (<4000/mm3) or Lymphopenia (<1000/mm3)",
  "Thrombocytopenia (<100,000/mm3)"
];

const SLICC_IMMUNOLOGIC = [
  "ANA above laboratory reference range",
  "Anti-dsDNA above laboratory reference range",
  "Anti-Sm presence",
  "Antiphospholipid antibody positivity",
  "Low complement (C3, C4, or CH50)",
  "Direct Coombs test positive in absence of hemolytic anemia"
];

const GAD7_QUESTIONS = {
  en: [
    "Feeling nervous, anxious or on edge",
    "Not being able to stop or control worrying",
    "Worrying too much about different things",
    "Trouble relaxing",
    "Being so restless that it is hard to sit still",
    "Becoming easily annoyed or irritable",
    "Feeling afraid as if something awful might happen"
  ],
  ar: [
    "الشعور بالعصبية، القلق، أو التوتر",
    "عدم القدرة على إيقاف أو السيطرة على القلق",
    "القلق المفرط بشأن أمور مختلفة",
    "صعوبة في الاسترخاء",
    "الشعور بالتململ لدرجة يصعب معها الجلوس بهدوء",
    "سهولة الانزعاج أو الاستثارة",
    "الشعور بالخوف كما لو أن شيئاً فظيعاً قد يحدث"
  ]
};

const CHA2DS2VASC_PARAMS = [
  { label: "Congestive Heart Failure", points: 1 },
  { label: "Hypertension", points: 1 },
  { label: "Age ≥ 75 years", points: 2 },
  { label: "Diabetes Mellitus", points: 1 },
  { label: "Stroke / TIA / Thromboembolism", points: 2 },
  { label: "Vascular Disease (Prior MI, PAD, aortic plaque)", points: 1 },
  { label: "Age 65 - 74 years", points: 1 },
  { label: "Sex Category (Female)", points: 1 }
];


const ABCDE_PARAMS = [
  { label: "A - Asymmetry (One half of the mole does not match the other)", ar: "A - عدم التماثل (نصف الشامة لا يطابق النصف الآخر)" },
  { label: "B - Border irregularity (Edges are ragged, notched, or blurred)", ar: "B - عدم انتظام الحدود (الحواف متعرجة أو غير واضحة)" },
  { label: "C - Color variation (Different shades of brown, black, red, etc.)", ar: "C - تباين اللون (درجات مختلفة من البني، الأسود، الأحمر، إلخ)" },
  { label: "D - Diameter > 6mm (Larger than a pencil eraser)", ar: "D - القطر > 6 مم (أكبر من ممحاة قلم رصاص)" },
  { label: "E - Evolving (Changing in size, shape, or color over time)", ar: "E - التطور (تغير في الحجم أو الشكل أو اللون بمرور الوقت)" }
];

const ACB_DRUGS = {
  level3: ["Amitriptyline", "Atropine", "Benztropine", "Chlorphenamine", "Chlorpromazine", "Clemastine", "Clomipramine", "Clozapine", "Darifenacin", "Desipramine", "Dicyclomine", "Diphenhydramine", "Doxepin", "Fesoterodine", "Flavoxate", "Hydroxyzine", "Imipramine", "Meclizine", "Nortriptyline", "Olanzapine", "Orphenadrine", "Oxybutynin", "Paroxetine", "Prochlorperazine", "Promethazine", "Quetiapine", "Scopolamine", "Solifenacin", "Tolterodine", "Trospium"],
  level2: ["Amantadine", "Belladonna", "Carbamazepine", "Cyclobenzaprine", "Cyproheptadine", "Loxapine", "Meperidine", "Methotrimeprazine", "Oxcarbazepine", "Pimozide"],
  level1: ["Aripiprazole", "Atenolol", "Bupropion", "Captopril", "Cetirizine", "Cimetidine", "Clindamycin", "Colchicine", "Desloratadine", "Diazepam", "Digoxin", "Fentanyl", "Furosemide", "Haloperidol", "Hydralazine", "Hydrocortisone", "Isosorbide", "Loperamide", "Metoprolol", "Morphine", "Nifedipine", "Prednisone", "Quinidine", "Ranitidine", "Trazodone", "Venlafaxine", "Warfarin"]
};
const ACT_QUESTIONS = {
  en: [
    "In the past 4 weeks, how much of the time did your asthma keep you from getting as much done at work, school or at home?",
    "During the past 4 weeks, how often have you had shortness of breath?",
    "During the past 4 weeks, how often did your asthma symptoms (wheezing, coughing, shortness of breath, chest tightness or pain) wake you up at night or earlier than usual in the morning?",
    "During the past 4 weeks, how often have you used your rescue inhaler or nebulizer medication (such as albuterol)?",
    "How would you rate your asthma control during the past 4 weeks?"
  ],
  ar: [
    "خلال الأسابيع الأربعة الماضية، كم من الوقت منعك الربو من إنجاز مهامك في العمل أو المدرسة أو المنزل بقدر ما تفعل عادة؟",
    "خلال الأسابيع الأربعة الماضية، كم مرة شعرت بضيق في التنفس؟",
    "خلال الأسابيع الأربعة الماضية، كم مرة أيقظتك أعراض الربو (أزيز، سعال، ضيق تنفس، ضيق في الصدر أو ألم) في الليل أو أبكر من المعتاد في الصباح؟",
    "خلال الأسابيع الأربعة الماضية، كم مرة استخدمت بخاخ الطوارئ أو جهاز التبخير (مثل الألبوتيرول)؟",
    "كيف تقيم مستوى السيطرة على الربو لديك خلال الأسابيع الأربعة الماضية؟"
  ]
};

const ACT_OPTIONS = {
  en: [
    ["All of the time", "Most of the time", "Some of the time", "A little of the time", "None of the time"],
    ["More than once a day", "Once a day", "3 to 6 times a week", "Once or twice a week", "Not at all"],
    ["4 or more nights a week", "2 to 3 nights a week", "Once a week", "Once or twice", "Not at all"],
    ["3 or more times per day", "1 or 2 times per day", "2 or 3 times per week", "Once a week or less", "Not at all"],
    ["Not controlled at all", "Poorly controlled", "Somewhat controlled", "Well controlled", "Completely controlled"]
  ],
  ar: [
    ["طوال الوقت", "معظم الوقت", "بعض الوقت", "قليلاً من الوقت", "ليس على الإطلاق"],
    ["أكثر من مرة في اليوم", "مرة في اليوم", "3 إلى 6 مرات في الأسبوع", "مرة أو مرتين في الأسبوع", "ليس على الإطلاق"],
    ["4 ليالٍ أو أكثر في الأسبوع", "2 إلى 3 ليالٍ في الأسبوع", "مرة في الأسبوع", "مرة أو مرتين", "ليس على الإطلاق"],
    ["3 مرات أو أكثر في اليوم", "مرة أو مرتين في اليوم", "مرتين أو 3 مرات في الأسبوع", "مرة في الأسبوع أو أقل", "ليس على الإطلاق"],
    ["غير مسيطر عليه إطلاقاً", "مسيطر عليه بشكل ضعيف", "مسيطر عليه نوعاً ما", "مسيطر عليه جيداً", "مسيطر عليه تماماً"]
  ]
};

const CAT_QUESTIONS = {
  en: [
    "I never cough / I cough all the time",
    "I have no phlegm (mucus) in my chest at all / My chest is completely full of phlegm (mucus)",
    "My chest does not feel tight at all / My chest feels very tight",
    "When I walk up a hill or one flight of stairs I am not breathless / When I walk up a hill or one flight of stairs I am very breathless",
    "I am not limited doing any activities at home / I am very limited doing activities at home",
    "I am confident leaving my home despite my lung condition / I am not at all confident leaving my home because of my lung condition",
    "I sleep soundly / I don't sleep soundly because of my lung condition",
    "I have lots of energy / I have no energy at all"
  ],
  ar: [
    "أنا لا أسعل أبداً / أنا أسعل طوال الوقت",
    "لا يوجد بلغم (مخاط) في صدري على الإطلاق / صدري ممتلئ تماماً بالبلغم (المخاط)",
    "لا أشعر بضيق في صدري على الإطلاق / أشعر بضيق شديد في صدري",
    "لا أشعر بضيق في التنفس عند صعود تلة أو طابق واحد من السلالم / أشعر بضيق شديد في التنفس عند صعود تلة أو طابق واحد من السلالم",
    "لست مقيداً في أداء أي أنشطة في المنزل / أنا مقيد جداً في أداء الأنشطة في المنزل",
    "أنا واثق من مغادرة منزلي بالرغم من حالة رئتي / لست واثقاً على الإطلاق من مغادرة منزلي بسبب حالة رئتي",
    "أنام بعمق / لا أنام بعمق بسبب حالة رئتي",
    "لدي الكثير من الطاقة / ليس لدي طاقة على الإطلاق"
  ]
};

const PUMA_QUESTIONS = {
  en: {
    gender: "Gender",
    age: "Age",
    packYears: "Smoking (Pack-Years)",
    dyspnea: "Do you usually feel short of breath when walking faster on level ground or climbing a slight hill?",
    sputum: "Do you usually bring up phlegm from your chest?",
    cough: "Do you usually cough?",
    spirometry: "Have you ever been asked by a doctor or other healthcare professional to perform a spirometry test?"
  },
  ar: {
    gender: "الجنس",
    age: "العمر",
    packYears: "التدخين (سنة-علبة)",
    dyspnea: "هل تشعر عادة بضيق في التنفس عند المشي بشكل أسرع على أرض مستوية أو صعود تلة خفيفة؟",
    sputum: "هل تخرج عادة بلغماً من صدرك؟",
    cough: "هل تسعل عادة؟",
    spirometry: "هل طلب منك طبيب أو أخصائي رعاية صحية إجراء فحص قياس التنفس (السبيرومتري) من قبل؟"
  }
};

const CATEGORIES_DEF = [
  { id: 'dermatology', label: 'Dermatology', icon: Activity, defaultCalc: 'dlqi' },
  { id: 'rheumatology', label: 'Systemic/Rheum', icon: Heart, defaultCalc: 'slicc' },
  { id: 'psychiatry', label: 'Psychiatry', icon: Brain, defaultCalc: 'phq9' },
    { id: 'respiratory', label: 'Respiratory', icon: Activity, defaultCalc: 'act' },
  { id: 'general', label: 'General', icon: FileText, defaultCalc: 'bmi' }
];

const SUB_TABS: Record<string, {id: string, label: string}[]> = {
  'dermatology': [
    { id: 'dlqi', label: 'DLQI' },
    { id: 'pasi', label: 'PASI' },
    { id: 'poem', label: 'POEM' },
    { id: 'easi', label: 'EASI' },
    { id: 'salt', label: 'SALT' },
    { id: 'bsa', label: 'BSA' },
    { id: 'uas7', label: 'UAS7' },
    { id: 'scorten', label: 'SCORTEN' },
    { id: 'abcde', label: 'ABCDE (Melanoma)' },
    { id: 'acb', label: 'ACB (Anticholinergic)' }
  ],
  'rheumatology': [
    { id: 'slicc', label: 'SLICC' },
    { id: 'basdai', label: 'BASDAI' },
    { id: 'pest', label: 'PEST' },
    { id: 'frax', label: 'FRAX' }
  ],
  'psychiatry': [
    { id: 'phq9', label: 'PHQ-9' },
    { id: 'mdq', label: 'MDQ' },
    { id: 'gad7', label: 'GAD-7' },
      { id: 'hads', label: 'HADS' }
    ],
    'respiratory': [
      { id: 'act', label: 'ACT (Asthma)' },
      { id: 'cat', label: 'CAT (COPD)' },
      { id: 'puma', label: 'PUMA Score (COPD)' }
    ],
    'general': [
    { id: 'bmi', label: 'BMI Calculator' },
    { id: 'egfr', label: 'eGFR' },
    { id: 'cha2ds2vasc', label: 'CHA2DS2-VASc' },
    { id: 'cvrisk', label: 'CV Risk (ASCVD)' }
  ]
};

export default function MedicalCalculators({ defaultCalculator, isPatientMode = false }: { defaultCalculator?: string, isPatientMode?: boolean } = {}) {
  const [sharingCalc, setSharingCalc] = useState<string>('');

  const handleSendToPatient = async (calcId: string) => {
    if (sharingCalc) return;
    setSharingCalc(calcId);
    try {
      const url = await createPatientLink(calcId);
      const text = `رجاءً قم بملء الاستبيان الطبي التالي قبل موعدك:\n${url}`;
      handleWhatsAppShare(text);
    } catch {
      alert('حدث خطأ أثناء إنشاء الرابط. يرجى المحاولة مجدداً.');
    } finally {
      setSharingCalc('');
    }
  };
  const initialTab = Object.keys(SUB_TABS).find(cat => SUB_TABS[cat].some(c => c.id === (defaultCalculator || 'dlqi'))) || 'dermatology';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activeCalculator, setActiveCalculator] = useState(defaultCalculator || 'dlqi');
  const [lang, setLang] = useState<'en' | 'ar'>('en');
  const [copied, setCopied] = useState('');
  const [activeGuide, setActiveGuide] = useState<string | null>(null);

  // Shared Print State
  const [patientName, setPatientName] = useState('');
  const [physicianName, setPhysicianName] = useState('');

  // === STATES ===
  const [bmiWeight, setBmiWeight] = useState('');
  const [bmiHeight, setBmiHeight] = useState('');

  // === RESPIRATORY STATES ===
  const [actScores, setActScores] = useState<number[]>(Array(5).fill(0));
  const [catScores, setCatScores] = useState<number[]>(Array(8).fill(-1));
  const [pumaState, setPumaState] = useState({ gender: '', age: '', packYears: '', dyspnea: '', sputum: '', cough: '', spirometry: '' });

  // dlqiScores removed due to external link
  const [phq9Scores, setPhq9Scores] = useState<number[]>(Array(9).fill(0));
  const [basdaiScores, setBasdaiScores] = useState<number[]>(Array(6).fill(0));
  const [mdqScores, setMdqScores] = useState({ q1: Array(13).fill(false), q2: false, q3: 0 });
  const [poemScores, setPoemScores] = useState<number[]>(Array(7).fill(0));

  const [gad7Scores, setGad7Scores] = useState<number[]>(Array(7).fill(0));
  // hadsScores removed due to external link
  const [pestScores, setPestScores] = useState<boolean[]>(Array(5).fill(false));
  const [uas7Scores, setUas7Scores] = useState<number[]>([0, 0, 0, 0, 0, 0, 0]);
  const [showEasiGuide, setShowEasiGuide] = useState(false);
  const [showPasiGuide, setShowPasiGuide] = useState(false);
  const [activePasiInfo, setActivePasiInfo] = useState<'redness' | 'thickness' | 'scale' | null>(null);

  // SALT State
  const [saltVertex, setSaltVertex] = useState<number | ''>('');
  const [saltRightProfile, setSaltRightProfile] = useState<number | ''>('');
  const [saltLeftProfile, setSaltLeftProfile] = useState<number | ''>('');
  const [saltPosterior, setSaltPosterior] = useState<number | ''>('');

  const [scortenScores, setScortenScores] = useState<boolean[]>(Array(7).fill(false));
  const [abcdeScores, setAbcdeScores] = useState<boolean[]>(Array(5).fill(false));

  // CV Risk State
  const [cvAge, setCvAge] = useState('');
  const [cvSex, setCvSex] = useState('male');
  const [cvRace, setCvRace] = useState('white');
  const [cvTc, setCvTc] = useState('');
  const [cvHdl, setCvHdl] = useState('');
  const [cvSbp, setCvSbp] = useState('');
  const [cvTreated, setCvTreated] = useState('no');
  const [cvSmoker, setCvSmoker] = useState('no');
  const [cvDiabetes, setCvDiabetes] = useState('no');
  const [cvUnit, setCvUnit] = useState('mg/dl');
  
  const [sliccClinical, setSliccClinical] = useState<boolean[]>(Array(11).fill(false));
  const [sliccImmunologic, setSliccImmunologic] = useState<boolean[]>(Array(6).fill(false));
  const [sliccLupusNephritis, setSliccLupusNephritis] = useState(false);

  const [acbLevel1, setAcbLevel1] = useState('');
  const [acbLevel2, setAcbLevel2] = useState('');
  const [acbLevel3, setAcbLevel3] = useState('');
  const [showAcbDrugs, setShowAcbDrugs] = useState(false);

  // PASI State
  const [pasiHead, setPasiHead] = useState({ e: 0, i: 0, d: 0, palms: 0 });
  const [pasiTrunk, setPasiTrunk] = useState({ e: 0, i: 0, d: 0, palms: 0 });
  const [pasiUpper, setPasiUpper] = useState({ e: 0, i: 0, d: 0, palms: 0 });
  const [pasiLower, setPasiLower] = useState({ e: 0, i: 0, d: 0, palms: 0 });
  
  // EASI State
  const [easiIsChild, setEasiIsChild] = useState(false);
  const [easiHead, setEasiHead] = useState({ e: 0, i: 0, ex: 0, l: 0, area: 0 });
  const [easiTrunk, setEasiTrunk] = useState({ e: 0, i: 0, ex: 0, l: 0, area: 0 });
  const [easiUpper, setEasiUpper] = useState({ e: 0, i: 0, ex: 0, l: 0, area: 0 });
  const [easiLower, setEasiLower] = useState({ e: 0, i: 0, ex: 0, l: 0, area: 0 });

  // UAS7 State
  const [uas7Wheals, setUas7Wheals] = useState<number[]>(Array(7).fill(0));
  const [uas7Itch, setUas7Itch] = useState<number[]>(Array(7).fill(0));

  // eGFR State
  const [egfrAge, setEgfrAge] = useState('');
  const [egfrSex, setEgfrSex] = useState<'Male'|'Female'>('Male');
  const [egfrCr, setEgfrCr] = useState('');
  
  // CHA2DS2-VASc State
  const [cha2ds2vascScores, setCha2ds2vascScores] = useState<boolean[]>(Array(8).fill(false));

  // === CALCULATORS ===
  const calculateCVRisk = () => {
      const age = parseFloat(cvAge);
      let tc = parseFloat(cvTc);
      let hdl = parseFloat(cvHdl);
      const sbp = parseFloat(cvSbp);
      if (isNaN(age) || isNaN(tc) || isNaN(hdl) || isNaN(sbp) || age <= 0 || tc <= 0 || hdl <= 0 || sbp <= 0) return null;
    
    if (cvUnit === 'mmol/l') {
      tc = tc * 38.67;
      hdl = hdl * 38.67;
    }
    
    const lnAge = Math.log(age);
    const lnTc = Math.log(tc);
    const lnHdl = Math.log(hdl);
    const lnSbp = Math.log(sbp);
    const smoker = cvSmoker === 'yes' ? 1 : 0;
    const dm = cvDiabetes === 'yes' ? 1 : 0;
    const treated = cvTreated === 'yes';

    let sum = 0;
    let meanSum = 0;
    let s0 = 0;

    if (cvRace === 'aa') {
      if (cvSex === 'male') {
        sum = 2.469 * lnAge + 0.302 * lnTc - 0.307 * lnHdl + (treated ? 1.916 : 1.809) * lnSbp + 0.549 * smoker + 0.645 * dm;
        meanSum = 19.54;
        s0 = 0.8954;
      } else {
        sum = 17.114 * lnAge + 0.940 * lnTc - 18.920 * lnHdl + 4.475 * lnAge * lnHdl + (treated ? 29.291 : 27.820) * lnSbp - (treated ? 6.432 : 6.087) * lnAge * lnSbp + 0.691 * smoker + 0.874 * dm;
        meanSum = 86.61;
        s0 = 0.9533;
      }
    } else {
      if (cvSex === 'male') {
        sum = 12.344 * lnAge + 11.853 * lnTc - 2.664 * lnAge * lnTc - 7.990 * lnHdl + 1.769 * lnAge * lnHdl + (treated ? 1.797 : 1.764) * lnSbp + 7.837 * smoker - 1.795 * lnAge * smoker + 0.658 * dm;
        meanSum = 61.18;
        s0 = 0.9144;
      } else {
        sum = -29.799 * lnAge + 4.884 * (lnAge ** 2) + 13.540 * lnTc - 3.114 * lnAge * lnTc - 13.578 * lnHdl + 3.149 * lnAge * lnHdl + (treated ? 2.019 : 1.957) * lnSbp + 7.574 * smoker - 1.665 * lnAge * smoker + 0.661 * dm;
        meanSum = -29.18;
        s0 = 0.9665;
      }
    }

    const risk = 1 - Math.pow(s0, Math.exp(sum - meanSum));
    const riskPercent = (risk * 100).toFixed(1);
    
    let riskLevel = 'Low Risk (<5%)';
    if (risk * 100 >= 5 && risk * 100 < 7.5) riskLevel = 'Borderline Risk (5-7.4%)';
    else if (risk * 100 >= 7.5 && risk * 100 < 20) riskLevel = 'Intermediate Risk (7.5-19.9%)';
    else if (risk * 100 >= 20) riskLevel = 'High Risk (≥20%)';

    return { score: riskPercent, risk: riskLevel };
  };

  const calculateBMI = () => {
    const w = parseFloat(bmiWeight);
    const h = parseFloat(bmiHeight) / 100;
    if (w > 0 && h > 0) {
      const bmi = w / (h * h);
      let cat = 'Normal weight';
      if (bmi < 18.5) cat = 'Underweight';
      else if (bmi >= 25 && bmi < 30) cat = 'Overweight';
      else if (bmi >= 30) cat = 'Obese';
      return { score: bmi.toFixed(1), category: cat };
    }
    return null;
  };

  const calculateBSA = () => {
    const w = parseFloat(bmiWeight);
    const h = parseFloat(bmiHeight); // cm
    if (w > 0 && h > 0) {
      const bsa = Math.sqrt((w * h) / 3600);
      return { score: bsa.toFixed(2) };
    }
    return null;
  };

  // calculateDLQI removed

  const calculatePHQ9 = () => {
    const score = phq9Scores.reduce((a, b) => a + b, 0);
    let severity = "Minimal depression";
    if (score >= 5 && score <= 9) severity = "Mild depression";
    else if (score >= 10 && score <= 14) severity = "Moderate depression";
    else if (score >= 15 && score <= 19) severity = "Moderately severe depression";
    else if (score >= 20) severity = "Severe depression";
    return { score, severity };
  };

  // calculateHADS removed

  const calculateGAD7 = () => {
    const score = gad7Scores.reduce((a, b) => a + b, 0);
    let severity = "Minimal Anxiety";
    if (score >= 5 && score <= 9) severity = "Mild Anxiety";
    else if (score >= 10 && score <= 14) severity = "Moderate Anxiety";
    else if (score >= 15) severity = "Severe Anxiety";
    return { score, severity };
  };

  const calculatePEST = () => {
    const score = pestScores.filter(Boolean).length;
    return { score, meaning: score >= 3 ? "Positive (Referral to Rheumatologist considered)" : "Negative" };
  };

  const calculateSCORTEN = () => {
    const score = scortenScores.filter(Boolean).length;
    let mortality = "3.2%";
    if (score === 1) mortality = "12.1%";
    else if (score === 2) mortality = "35.3%";
    else if (score === 3) mortality = "58.3%";
    else if (score === 4) mortality = "85.3%";
    else if (score >= 5) mortality = ">90%";
    return { score, mortality };
  };

  const calculateSLICC = () => {
    const cCount = sliccClinical.filter(Boolean).length;
    const iCount = sliccImmunologic.filter(Boolean).length;
    const total = cCount + iCount;
    let diag = "SLE Criteria NOT Met";
    if (sliccLupusNephritis && sliccImmunologic.includes(true)) {
      diag = "SLE Criteria MET (Biopsy proven lupus nephritis + ANA/Anti-dsDNA)";
    } else if (total >= 4 && cCount >= 1 && iCount >= 1) {
      diag = "SLE Criteria MET (≥4 criteria including at least 1 clinical and 1 immunologic)";
    }
    return { clinical: cCount, immunologic: iCount, total, diagnosis: diag };
  };

  const calculateUAS7 = () => {
    const whealsScore = uas7Wheals.reduce((a, b) => a + b, 0);
    const itchScore = uas7Itch.reduce((a, b) => a + b, 0);
    const total = whealsScore + itchScore;
    let severity = 'Well Controlled';
    if (total >= 28) severity = 'Severe Urticaria';
    else if (total >= 16) severity = 'Moderate Urticaria';
    else if (total >= 7) severity = 'Mild Urticaria';
    return { score: total, wheals: whealsScore, itch: itchScore, severity };
  };

  const calculateEGFR = () => {
    const age = parseFloat(egfrAge);
    const cr = parseFloat(egfrCr);
    if (!age || !cr) return null;
    
    const k = egfrSex === 'Female' ? 0.7 : 0.9;
    const alpha = egfrSex === 'Female' ? -0.241 : -0.302;
    const multiplier = egfrSex === 'Female' ? 1.012 : 1;
    
    const min = Math.min(cr / k, 1);
    const max = Math.max(cr / k, 1);
    
    const egfr = 142 * Math.pow(min, alpha) * Math.pow(max, -1.2) * Math.pow(0.9938, age) * multiplier;
    
    let stage = 'G1 (Normal)';
    if (egfr < 15) stage = 'G5 (Kidney Failure)';
    else if (egfr < 30) stage = 'G4 (Severe Decrease)';
    else if (egfr < 45) stage = 'G3b (Mod-Severe Decrease)';
    else if (egfr < 60) stage = 'G3a (Mild-Mod Decrease)';
    else if (egfr < 90) stage = 'G2 (Mild Decrease)';
    
    return { score: Math.round(egfr), stage };
  };

  const calculateCHA2DS2VASC = () => {
    let score = 0;
    cha2ds2vascScores.forEach((checked, i) => {
      if (checked) score += CHA2DS2VASC_PARAMS[i].points;
    });
    
    let risk = 'Low Risk';
    if (score >= 2) risk = 'High Risk';
    else if (score === 1) risk = 'Moderate Risk';
    
    return { score, risk };
  };


  const calculateABCDE = () => {
    const score = abcdeScores.filter(Boolean).length;
    let risk = 'Benign appearing';
    if (score >= 1) risk = 'Suspicious for Melanoma (Consider Biopsy)';
    return { score, risk };
  };

    const calculateACB = () => {
    const l1 = parseInt(acbLevel1) || 0;
    const l2 = parseInt(acbLevel2) || 0;
    const l3 = parseInt(acbLevel3) || 0;
    const score = l1 * 1 + l2 * 2 + l3 * 3;
    return { score, meaning: score >= 3 ? "High Risk (Increased risk of cognitive impairment)" : "Low Risk" };
  };

  const calculatePASIAreaScore = (palms: number, maxPalms: number) => {
    const percent = (palms / maxPalms) * 100;
    if (percent === 0) return 0;
    if (percent < 10) return 1;
    if (percent < 30) return 2;
    if (percent < 50) return 3;
    if (percent < 70) return 4;
    if (percent < 90) return 5;
    return 6;
  };

  const getPasiRegionDetails = (state: {e:number, i:number, d:number, palms:number}, maxPalms: number, multiplier: number) => {
    const areaScore = calculatePASIAreaScore(state.palms, maxPalms);
    const total = multiplier * (state.e + state.i + state.d) * areaScore;
    return { areaScore, total };
  };

  const getEasiRegionDetails = (state: {e:number, i:number, ex:number, l:number, area:number}, multiplier: number) => {
    const signsSum = state.e + state.i + state.ex + state.l;
    const total = signsSum * state.area * multiplier;
    return { signsSum, total };
  };

  const calculateBASDAI = () => {
    const q1to4 = basdaiScores.slice(0, 4).reduce((a, b) => a + b, 0);
    const q5q6 = (basdaiScores[4] + basdaiScores[5]) / 2;
    const score = (q1to4 + q5q6) / 5;
    const meaning = score >= 4 ? (lang === 'ar' ? 'نشاط مرضي عالي (Suboptimal control)' : 'Suboptimal control (Active disease)') : (lang === 'ar' ? 'سيطرة جيدة (Good control)' : 'Good control');
    return { score: score.toFixed(1), meaning };
  }

  const calculateMDQ = () => {
    const q1Count = mdqScores.q1.filter(Boolean).length;
    const isPositive = q1Count >= 7 && mdqScores.q2 && mdqScores.q3 >= 2;
    const meaning = isPositive ? (lang === 'ar' ? 'نتيجة إيجابية (Positive Screen)' : 'Positive Screen (Bipolar likely)') : (lang === 'ar' ? 'نتيجة سلبية (Negative Screen)' : 'Negative Screen');
    return { score: q1Count, isPositive, meaning };
  }

  const calculatePOEM = () => {
    const score = poemScores.reduce((a, b) => a + b, 0);
    let meaning = '';
    if (score <= 2) meaning = lang === 'ar' ? 'صافي تقريباً (Clear)' : 'Clear or almost clear';
    else if (score <= 7) meaning = lang === 'ar' ? 'خفيف (Mild)' : 'Mild eczema';
    else if (score <= 16) meaning = lang === 'ar' ? 'متوسط (Moderate)' : 'Moderate eczema';
    else if (score <= 24) meaning = lang === 'ar' ? 'شديد (Severe)' : 'Severe eczema';
    else meaning = lang === 'ar' ? 'شديد جداً (Very Severe)' : 'Very severe eczema';
    return { score, meaning };
  }

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    try {
      const bc = new BroadcastChannel('iq_doctor_calculators');
      bc.postMessage({ type: 'ADD_NOTE', text });
      bc.close();
    } catch (e) {}
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleWhatsAppShare = (text: string) => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans" dir="ltr">
      <div className="max-w-7xl mx-auto px-4 py-8">
        
        <div className="mb-8 bg-white p-6 rounded-2xl shadow-sm border border-slate-200 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-5">
             <Calculator className="w-32 h-32" />
          </div>
          <div className="flex justify-between items-center mb-4 relative z-10 print:hidden">
            {!isPatientMode && (
              <button
                onClick={() => window.location.hash = ''}
                className="flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full font-bold text-sm transition shadow-sm"
              >
                <Home className="w-4 h-4" />
                العودة إلى الرئيسية
              </button>
            )}
            {isPatientMode && <div />}
            <button 
              onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
              className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-full font-bold text-sm transition"
            >
              <Globe className="w-4 h-4" />
              {lang === 'en' ? 'ترجمة الأسئلة للعربية' : 'Translate to English'}
            </button>
          </div>
          <h1 className="text-3xl font-extrabold text-slate-800 mb-2 font-serif text-center" dir="rtl">
            Clinical Calculators & Scores
          </h1>
        </div>

      {!isPatientMode && (
        <>
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-2 border-b border-slate-200 pb-2 print:hidden">
            {CATEGORIES_DEF.map(cat => (
              <button
                key={cat.id}
                onClick={() => { setActiveTab(cat.id); setActiveCalculator(cat.defaultCalc); }}
                className={`px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 transition ${activeTab === cat.id ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
              >
                <cat.icon className="w-4 h-4" /> {cat.label}
              </button>
            ))}
          </div>

          {/* Sub-Tabs for Active Category */}
          <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-4 print:hidden">
            {SUB_TABS[activeTab]?.map(calc => (
              <button key={calc.id} onClick={() => setActiveCalculator(calc.id)} className={`px-4 py-1.5 rounded-full text-sm font-bold transition ${activeCalculator === calc.id ? 'bg-indigo-100 text-indigo-700 border border-indigo-300' : 'bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100'}`}>{calc.label}</button>
            ))}
          </div>
        </>
      )}

      {/* Generic Print Header Component */}
      {!isPatientMode && (
        <div className="p-6 bg-white border border-slate-200 rounded-2xl mb-6 shadow-sm print:hidden">
           <h3 className="text-indigo-600 font-bold mb-4">Patient Information (For Printing)</h3>
           <div className="flex flex-col md:flex-row gap-4 items-end">
             <div className="flex-1 w-full">
               <label className="block text-sm font-bold text-slate-500 mb-1">Patient Name</label>
               <input type="text" value={patientName} onChange={e => setPatientName(e.target.value)} className="w-full border border-slate-300 rounded p-2 focus:border-indigo-500 outline-none" />
             </div>
             <div className="flex-1 w-full">
               <label className="block text-sm font-bold text-slate-500 mb-1">Physician Name</label>
               <input type="text" value={physicianName} onChange={e => setPhysicianName(e.target.value)} className="w-full border border-slate-300 rounded p-2 focus:border-indigo-500 outline-none" />
             </div>
             <button onClick={() => window.print()} className="bg-slate-800 hover:bg-slate-900 text-white px-6 py-2 rounded shadow-sm font-bold whitespace-nowrap">Print Report</button>
           </div>
          </div>
      )}

        {/* Print Only Header */}
        <div className="hidden print:block mb-8 border-b border-slate-300 pb-4">
           <h1 className="text-2xl font-black text-slate-800 mb-4">Clinical Score Report</h1>
           <div className="grid grid-cols-2 gap-4 text-sm">
             <div><strong>Patient Name:</strong> {patientName || '______________________'}</div>
             <div><strong>Physician:</strong> {physicianName || '______________________'}</div>
             <div><strong>Date:</strong> {new Date().toLocaleDateString()}</div>
           </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
{activeCalculator === 'basdai' && (<>
              <div className="bg-white/40 backdrop-blur-xl rounded-3xl shadow-xl shadow-indigo-900/5 border border-white/60 overflow-hidden relative">
                <div className="px-6 py-6 border-b border-white/50 flex flex-col md:flex-row md:justify-between md:items-center gap-4 relative z-10 bg-white/20">
                  <h2 className="text-xl font-extrabold text-slate-800 flex flex-wrap items-center gap-3 w-full">
  <span className="flex items-center gap-2">
    BASDAI
    <button onClick={() => setActiveGuide("basdai")} className="inline-flex items-center justify-center p-1.5 text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors group relative">
      <Info className="w-5 h-5" />
      <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-slate-800 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none">
        {lang === "ar" ? "الدليل السريري" : "Clinical Guide"}
      </span>
    </button>
  </span>
  {!isPatientMode && (
    <button onClick={() => handleSendToPatient('basdai')} disabled={!!sharingCalc} className="text-xs font-bold flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full hover:bg-green-100 transition border border-green-200 ml-auto rtl:ml-0 rtl:mr-auto disabled:opacity-60 disabled:cursor-wait">
      {sharingCalc === 'basdai' ? (
        <span className="w-3.5 h-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin inline-block" />
      ) : (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
      )}
      {sharingCalc === 'basdai' ? (lang === 'ar' ? 'جاري الإنشاء...' : 'Generating...') : (lang === 'ar' ? 'إرسال للمريض (واتساب)' : 'Send to Patient')}
    </button>
  )}
</h2>
                  {(() => {
                    const res = calculateBASDAI();
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score}/10</div>
                          <div className="text-xs font-bold text-slate-500">{res.meaning}</div>
                        </div>
                        <div className="flex items-center space-x-2 rtl:space-x-reverse border-l border-slate-300 pl-4 rtl:pl-0 rtl:border-l-0 rtl:border-r rtl:pr-4">
                          <button onClick={() => handleWhatsAppShare(isPatientMode ? `نتيجة المريض - BASDAI:\nالنتيجة: ${res.score}/10\nالتشخيص: ${res.meaning}` : `BASDAI Score: ${res.score}/10 - ${res.meaning}`)} className="p-2.5 text-emerald-600 hover:text-white hover:bg-emerald-500 bg-white rounded-xl shadow-sm border border-slate-200 transition-all">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                          </button>
                          <button onClick={() => setBasdaiScores(Array(6).fill(0))} className="p-2.5 text-rose-500 hover:text-white hover:bg-rose-500 bg-white rounded-xl shadow-sm border border-slate-200 transition-all">
                            <RefreshCw className="w-5 h-5" />
                          </button>
                          <button onClick={() => handleCopy(`BASDAI Score: ${res.score}/10 - ${res.meaning}`, 'basdai')} className="p-2.5 text-indigo-500 hover:text-white hover:bg-indigo-500 bg-white rounded-xl shadow-sm border border-slate-200 transition-all">
                            {copied === 'basdai' ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    )
                  })()}
                </div>
                
                <div className="p-6 space-y-6 relative z-10" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                  {BASDAI_QUESTIONS[lang].map((q, i) => (
                    <div key={i} className="bg-white/60 p-5 rounded-2xl border border-white/80 shadow-sm">
                      <label className="block font-medium text-slate-700 mb-4">{q}</label>
                      {i === 5 && (
                        <p className="text-xs text-amber-600 font-semibold mb-2" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                          {lang === 'ar'
                            ? '(0 = لا تيبس، 5 = ساعة واحدة، 10 = ساعتان أو أكثر)'
                            : '(0 = No stiffness, 5 ≈ 1 hour, 10 = 2 hours or more)'}
                        </p>
                      )}
                      <div className="flex items-center gap-4">
                        <span className="text-xs font-bold text-slate-400">{i === 5 ? '0 hrs' : 'None'}</span>
                        <input type="range" min="0" max="10" step="1" value={basdaiScores[i]} onChange={(e) => {
                          const newS = [...basdaiScores];
                          newS[i] = Number(e.target.value);
                          setBasdaiScores(newS);
                        }} className="flex-1 accent-indigo-600" />
                        <span className="text-xs font-bold text-slate-400">{i === 5 ? '2+ hrs' : 'Very Severe'}</span>
                        <span className="font-bold text-indigo-700 w-6 text-center">{basdaiScores[i]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
          </>)}

          {activeCalculator === 'mdq' && (<>
              <div className="bg-white/40 backdrop-blur-xl rounded-3xl shadow-xl shadow-indigo-900/5 border border-white/60 overflow-hidden relative">
                <div className="px-6 py-6 border-b border-white/50 flex flex-col md:flex-row md:justify-between md:items-center gap-4 relative z-10 bg-white/20">
                  <h2 className="text-xl font-extrabold text-slate-800 flex flex-wrap items-center gap-3 w-full">
  <span className="flex items-center gap-2">
    MDQ (Mood Disorder Questionnaire)
    <button onClick={() => setActiveGuide("mdq")} className="inline-flex items-center justify-center p-1.5 text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors group relative">
      <Info className="w-5 h-5" />
      <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-slate-800 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none">
        {lang === "ar" ? "الدليل السريري" : "Clinical Guide"}
      </span>
    </button>
  </span>
  {!isPatientMode && (
    <button onClick={() => handleSendToPatient('mdq')} disabled={!!sharingCalc} className="text-xs font-bold flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full hover:bg-green-100 transition border border-green-200 ml-auto rtl:ml-0 rtl:mr-auto disabled:opacity-60 disabled:cursor-wait">
      {sharingCalc === 'mdq' ? (
        <span className="w-3.5 h-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin inline-block" />
      ) : (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
      )}
      {sharingCalc === 'mdq' ? (lang === 'ar' ? 'جاري الإنشاء...' : 'Generating...') : (lang === 'ar' ? 'إرسال للمريض (واتساب)' : 'Send to Patient')}
    </button>
  )}
</h2>
                  {(() => {
                    const res = calculateMDQ();
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score}/13</div>
                          <div className={`text-xs font-bold ${res.isPositive ? 'text-rose-500' : 'text-emerald-500'}`}>{res.meaning}</div>
                        </div>
                        <div className="flex items-center space-x-2 rtl:space-x-reverse border-l border-slate-300 pl-4 rtl:pl-0 rtl:border-l-0 rtl:border-r rtl:pr-4">
                          <button onClick={() => handleWhatsAppShare(isPatientMode ? `نتيجة المريض - MDQ:\nالنتيجة: ${res.score}/13\nالتشخيص: ${res.meaning}` : `MDQ Score: ${res.score}/13 - ${res.meaning}`)} className="p-2.5 text-emerald-600 hover:text-white hover:bg-emerald-500 bg-white rounded-xl shadow-sm border border-slate-200 transition-all">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                          </button>
                          <button onClick={() => setMdqScores({ q1: Array(13).fill(false), q2: false, q3: 0 })} className="p-2.5 text-rose-500 hover:text-white hover:bg-rose-500 bg-white rounded-xl shadow-sm border border-slate-200 transition-all">
                            <RefreshCw className="w-5 h-5" />
                          </button>
                          <button onClick={() => handleCopy(`MDQ Score: ${res.score}/13 - ${res.meaning}`, 'mdq')} className="p-2.5 text-indigo-500 hover:text-white hover:bg-indigo-500 bg-white rounded-xl shadow-sm border border-slate-200 transition-all">
                            {copied === 'mdq' ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    )
                  })()}
                </div>
                
                <div className="p-6 space-y-8 relative z-10" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                  <div className="space-y-4">
                    <h3 className="font-bold text-slate-800 text-lg border-b pb-2">{lang === 'ar' ? 'السؤال 1: هل حدثت لك هذه الأشياء؟' : 'Question 1: Has there ever been a period of time when you were not your usual self and...'}</h3>
                    {MDQ_QUESTIONS[lang].map((q, i) => (
                      <label key={i} className={`flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition ${mdqScores.q1[i] ? 'bg-indigo-500/10 border-indigo-500 shadow-sm' : 'bg-white/60 border-white/80 hover:bg-white'}`}>
                        <input type="checkbox" className="w-5 h-5 mt-0.5 text-indigo-600 rounded" checked={mdqScores.q1[i]} onChange={(e) => {
                          const newQ1 = [...mdqScores.q1];
                          newQ1[i] = e.target.checked;
                          setMdqScores({...mdqScores, q1: newQ1});
                        }} />
                        <span className="font-medium text-slate-700 leading-relaxed">{q}</span>
                      </label>
                    ))}
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-bold text-slate-800 text-lg border-b pb-2">{lang === 'ar' ? 'السؤال 2' : 'Question 2'}</h3>
                    <label className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition ${mdqScores.q2 ? 'bg-indigo-500/10 border-indigo-500 shadow-sm' : 'bg-white/60 border-white/80'}`}>
                      <input type="checkbox" className="w-5 h-5 text-indigo-600 rounded" checked={mdqScores.q2} onChange={(e) => setMdqScores({...mdqScores, q2: e.target.checked})} />
                      <span className="font-medium text-slate-700">
                        {lang === 'ar' ? 'إذا اخترت "نعم" لأكثر من عنصر أعلاه، هل حدثت العديد من هذه الأشياء خلال نفس الفترة الزمنية؟' : 'If you checked YES to more than one of the above, have several of these ever happened during the same period of time?'}
                      </span>
                    </label>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-bold text-slate-800 text-lg border-b pb-2">{lang === 'ar' ? 'السؤال 3' : 'Question 3'}</h3>
                    <p className="text-slate-600">{lang === 'ar' ? 'إلى أي مدى سببت لك هذه الأشياء مشاكل (كالقدرة على العمل، ومشاكل عائلية، ومالية أو قانونية)؟' : 'How much of a problem did any of these cause you?'}</p>
                    <div className="flex flex-wrap gap-3">
                      {[
                        {v:0, en:'No Problem', ar:'لا توجد مشكلة'},
                        {v:1, en:'Minor Problem', ar:'مشكلة بسيطة'},
                        {v:2, en:'Moderate Problem', ar:'مشكلة متوسطة'},
                        {v:3, en:'Serious Problem', ar:'مشكلة خطيرة'}
                      ].map((opt) => (
                        <button key={opt.v} onClick={() => setMdqScores({...mdqScores, q3: opt.v})} className={`px-5 py-2.5 rounded-xl font-bold transition ${mdqScores.q3 === opt.v ? 'bg-indigo-600 text-white shadow-md' : 'bg-white/60 text-slate-600 hover:bg-white'}`}>
                          {lang === 'ar' ? opt.ar : opt.en}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
          </>)}

          {activeCalculator === 'poem' && (<>
              <div className="bg-white/40 backdrop-blur-xl rounded-3xl shadow-xl shadow-indigo-900/5 border border-white/60 overflow-hidden relative">
                <div className="px-6 py-6 border-b border-white/50 flex flex-col md:flex-row md:justify-between md:items-center gap-4 relative z-10 bg-white/20">
                  <h2 className="text-xl font-extrabold text-slate-800 flex flex-wrap items-center gap-3 w-full">
  <span className="flex items-center gap-2">
    POEM (Patient-Oriented Eczema Measure)
    <button onClick={() => setActiveGuide("poem")} className="inline-flex items-center justify-center p-1.5 text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors group relative">
      <Info className="w-5 h-5" />
      <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-slate-800 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none">
        {lang === "ar" ? "الدليل السريري" : "Clinical Guide"}
      </span>
    </button>
  </span>
  {!isPatientMode && (
    <button onClick={() => handleSendToPatient('poem')} disabled={!!sharingCalc} className="text-xs font-bold flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full hover:bg-green-100 transition border border-green-200 ml-auto rtl:ml-0 rtl:mr-auto disabled:opacity-60 disabled:cursor-wait">
      {sharingCalc === 'poem' ? (
        <span className="w-3.5 h-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin inline-block" />
      ) : (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
      )}
      {sharingCalc === 'poem' ? (lang === 'ar' ? 'جاري الإنشاء...' : 'Generating...') : (lang === 'ar' ? 'إرسال للمريض (واتساب)' : 'Send to Patient')}
    </button>
  )}
</h2>
                  {(() => {
                    const res = calculatePOEM();
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score}/28</div>
                          <div className="text-xs font-bold text-slate-500">{res.meaning}</div>
                        </div>
                        <div className="flex items-center space-x-2 rtl:space-x-reverse border-l border-slate-300 pl-4 rtl:pl-0 rtl:border-l-0 rtl:border-r rtl:pr-4">
                          <button onClick={() => handleWhatsAppShare(isPatientMode ? `نتيجة المريض - POEM:\nالنتيجة: ${res.score}/28\nالتشخيص: ${res.meaning}` : `POEM Score: ${res.score}/28 - ${res.meaning}`)} className="p-2.5 text-emerald-600 hover:text-white hover:bg-emerald-500 bg-white rounded-xl shadow-sm border border-slate-200 transition-all">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                          </button>
                          <button onClick={() => setPoemScores(Array(7).fill(0))} className="p-2.5 text-rose-500 hover:text-white hover:bg-rose-500 bg-white rounded-xl shadow-sm border border-slate-200 transition-all">
                            <RefreshCw className="w-5 h-5" />
                          </button>
                          <button onClick={() => handleCopy(`POEM Score: ${res.score}/28 - ${res.meaning}`, 'poem')} className="p-2.5 text-indigo-500 hover:text-white hover:bg-indigo-500 bg-white rounded-xl shadow-sm border border-slate-200 transition-all">
                            {copied === 'poem' ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    )
                  })()}
                </div>
                
                <div className="p-6 space-y-6 relative z-10" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                  <p className="text-slate-500 font-medium mb-4">{lang === 'ar' ? 'خلال الأسبوع الماضي (الأيام السبعة الماضية):' : 'Over the last week (7 days):'}</p>
                  {POEM_QUESTIONS[lang].map((q, i) => (
                    <div key={i} className="bg-white/60 p-5 rounded-2xl border border-white/80 shadow-sm">
                      <label className="block font-medium text-slate-700 mb-4">{q}</label>
                      <div className="flex flex-wrap gap-2">
                        {POEM_OPTIONS[lang].map((opt) => (
                          <button 
                            key={opt.value}
                            onClick={() => {
                              const newS = [...poemScores];
                              newS[i] = opt.value;
                              setPoemScores(newS);
                            }}
                            className={`flex-1 min-w-[100px] px-3 py-2.5 text-sm font-bold rounded-xl transition ${poemScores[i] === opt.value ? 'bg-indigo-600 text-white shadow-md' : 'bg-white/80 text-slate-600 hover:bg-white hover:text-indigo-600 border border-slate-200'}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
          </>)}

          {activeCalculator === 'pasi' && (<>
              {/* PASI Score */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center print:hidden">
                  <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 flex-wrap">
                    PASI (Psoriasis Area and Severity Index)
                    <button onClick={() => setActiveGuide('pasi')} className="text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 p-1 rounded-full transition" title={lang === 'ar' ? "الدليل السريري" : "Clinical Guide"}>
                      <Info className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => setShowPasiGuide(true)}
                      className="ml-4 flex items-center gap-1.5 px-3 py-1.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 hover:text-indigo-800 rounded-full text-xs font-bold transition-colors"
                      title="Show User Guide & Atlas"
                    >
                      <BookOpen className="w-4 h-4" />
                      Show Guide & Atlas
                    </button>
                  </h2>
                </div>

                {(() => {
                  const headRes = getPasiRegionDetails(pasiHead, 10, 0.1);
                  const upperRes = getPasiRegionDetails(pasiUpper, 20, 0.2);
                  const trunkRes = getPasiRegionDetails(pasiTrunk, 30, 0.3);
                  const lowerRes = getPasiRegionDetails(pasiLower, 40, 0.4);
                  const totalPasi = headRes.total + upperRes.total + trunkRes.total + lowerRes.total;

                  return (
                    <div className="p-6">
                      <div className="text-3xl font-bold text-slate-500 mb-6 flex items-center gap-2">
                         PASI SCORE: <span className="text-orange-600">{totalPasi.toFixed(1)}</span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[
                          { id: 'head', title: 'Head and Neck', maxPalms: 10, state: pasiHead, setter: setPasiHead, res: headRes },
                          { id: 'upper', title: 'Upper Extremities', maxPalms: 20, state: pasiUpper, setter: setPasiUpper, res: upperRes },
                          { id: 'trunk', title: 'Trunk', maxPalms: 30, state: pasiTrunk, setter: setPasiTrunk, res: trunkRes },
                          { id: 'lower', title: 'Lower Extremities', maxPalms: 40, state: pasiLower, setter: setPasiLower, res: lowerRes },
                        ].map((region) => (
                          <div key={region.id} className="border border-slate-300 rounded bg-slate-50 overflow-hidden flex flex-col print:border-slate-400">
                            <div className="bg-slate-300 px-3 py-2 font-bold text-slate-800 print:bg-slate-200">{region.title}</div>
                            <div className="flex bg-white h-full">
                               <div className="w-2/3 border-r border-slate-200">
                                 <table className="w-full text-sm">
                                   <tbody>
                                     <tr className="border-b border-slate-100">
                                       <td className="p-2 text-blue-400 font-semibold text-[11px] uppercase w-1/2">Skin Area Involved</td>
                                       <td className="p-2 font-bold flex items-center gap-1">
                                         <select 
                                            className="border border-slate-200 rounded p-0.5 bg-white outline-none print:appearance-none print:border-none"
                                            value={region.state.palms}
                                            onChange={(e) => region.setter({ ...region.state, palms: parseInt(e.target.value) })}
                                          >
                                            {Array.from({ length: region.maxPalms + 1 }).map((_, v) => <option key={v} value={v}>{v}</option>)}
                                          </select> <span className="text-slate-500 font-normal text-xs">palms</span>
                                       </td>
                                     </tr>
                                     <tr className="border-b border-slate-100 bg-slate-50">
                                       <td className="p-2 text-blue-400 font-semibold text-[11px] uppercase">Area Score</td>
                                       <td className="p-2 font-black text-slate-800">{region.res.areaScore}</td>
                                     </tr>
                                     {[
                                       { key: 'e', label: 'Redness', type: 'redness' as const },
                                       { key: 'i', label: 'Thickness', type: 'thickness' as const },
                                       { key: 'd', label: 'Scale', type: 'scale' as const },
                                     ].map(grade => (
                                       <tr key={grade.key} className="border-b border-slate-100">
                                         <td className="p-2 text-blue-400 font-semibold text-[11px] uppercase">{grade.label}</td>
                                         <td className="p-2 font-bold flex items-center gap-2">
                                            <select 
                                              className="border border-slate-200 rounded p-0.5 bg-white outline-none print:appearance-none print:border-none"
                                              value={(region.state as any)[grade.key]}
                                              onChange={(e) => region.setter({ ...region.state, [grade.key]: parseInt(e.target.value) })}
                                            >
                                              {Array.from({ length: 5 }).map((_, v) => <option key={v} value={v}>{v}</option>)}
                                            </select>
                                            <button 
                                              onClick={() => setActivePasiInfo(grade.type)}
                                              className="text-blue-400 hover:text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-full p-1 print:hidden transition-colors"
                                              title={`Info about ${grade.label}`}
                                            >
                                              <Info className="w-4 h-4" />
                                            </button>
                                         </td>
                                       </tr>
                                     ))}
                                     <tr className="border-t-2 border-slate-200 bg-slate-50">
                                       <td className="p-2 font-black text-slate-800 text-xs">TOTAL:</td>
                                       <td className="p-2 font-black text-slate-800">{region.res.total.toFixed(1)}</td>
                                     </tr>
                                   </tbody>
                                 </table>
                               </div>
                               <div className="w-1/3 flex items-center justify-center p-2 bg-slate-50/30">
                                 <svg viewBox="0 0 100 220" className="w-full max-w-[80px] h-auto drop-shadow-sm">
                                   <g stroke="#cbd5e1" strokeWidth="1" strokeLinejoin="round">
                                     <path d="M40,15 C40,0 60,0 60,15 L60,35 C55,40 45,40 40,35 Z" fill={region.id === 'head' ? '#ff4d4f' : '#f1f5f9'} />
                                     <path d="M30,35 C35,40 65,40 70,35 L65,110 C50,115 50,115 35,110 Z" fill={region.id === 'trunk' ? '#ff4d4f' : '#f1f5f9'} />
                                     <path d="M30,35 C15,40 10,60 15,90 C18,100 25,95 28,85 L30,45 Z" fill={region.id === 'upper' ? '#ff4d4f' : '#f1f5f9'} />
                                     <path d="M70,35 C85,40 90,60 85,90 C82,100 75,95 72,85 L70,45 Z" fill={region.id === 'upper' ? '#ff4d4f' : '#f1f5f9'} />
                                     <path d="M35,110 L48,110 L45,210 C40,215 35,215 35,210 Z" fill={region.id === 'lower' ? '#ff4d4f' : '#f1f5f9'} />
                                     <path d="M52,110 L65,110 L65,210 C65,215 60,215 55,210 Z" fill={region.id === 'lower' ? '#ff4d4f' : '#f1f5f9'} />
                                   </g>
                                 </svg>
                               </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                <div className="bg-slate-50 px-6 py-2 border-t border-slate-200 flex justify-between items-center print:hidden">
                  <span className="text-sm font-bold text-slate-600">Grading Scale Reference</span>
                  <button onClick={() => setShowPasiGuide(!showPasiGuide)} className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition">
                    {showPasiGuide ? 'Hide Guide' : 'Show Guide'}
                  </button>
                </div>
                {showPasiGuide && (
                  <div className="p-6 bg-indigo-50 border-b border-indigo-100 text-xs grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div>
                      <h4 className="font-bold text-indigo-900 mb-2 border-b border-indigo-200 pb-1">Erythema (Redness)</h4>
                      <ul className="space-y-1.5 text-indigo-800">
                        <li><span className="inline-block w-3 h-3 rounded-full bg-slate-200 mr-1"></span><strong>0:</strong> No Redness</li>
                        <li><span className="inline-block w-3 h-3 rounded-full bg-pink-200 mr-1"></span><strong>1:</strong> Light Red</li>
                        <li><span className="inline-block w-3 h-3 rounded-full bg-red-400 mr-1"></span><strong>2:</strong> Red, but not Deep Red</li>
                        <li><span className="inline-block w-3 h-3 rounded-full bg-red-600 mr-1"></span><strong>3:</strong> Very Red</li>
                        <li><span className="inline-block w-3 h-3 rounded-full bg-red-800 mr-1"></span><strong>4:</strong> Extremely Red</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-indigo-900 mb-2 border-b border-indigo-200 pb-1">Induration (Thickness)</h4>
                      <ul className="space-y-1.5 text-indigo-800">
                        <li><strong>0:</strong> No Thickness</li>
                        <li><strong>1:</strong> Mild (0.25 mm)</li>
                        <li><strong>2:</strong> Moderate (0.5 mm)</li>
                        <li><strong>3:</strong> Severe (1 mm)</li>
                        <li><strong>4:</strong> Very Severe (&gt;1.25 mm)</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-indigo-900 mb-2 border-b border-indigo-200 pb-1">Desquamation (Scaling)</h4>
                      <ul className="space-y-1.5 text-indigo-800">
                        <li><strong>0:</strong> No Scale</li>
                        <li><strong>1:</strong> Mainly fine scale</li>
                        <li><strong>2:</strong> Coarser, thin scale</li>
                        <li><strong>3:</strong> Coarser, thick scale</li>
                        <li><strong>4:</strong> Very thick scale</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-indigo-900 mb-2 border-b border-indigo-200 pb-1">Area (Palm method)</h4>
                      <ul className="space-y-1.5 text-indigo-800">
                        <li><strong>0:</strong> 0 palms (0%)</li>
                        <li><strong>1:</strong> 1-9 palms (1-9%)</li>
                        <li><strong>2:</strong> 10-29%</li>
                        <li><strong>3:</strong> 30-49%</li>
                        <li><strong>4:</strong> 50-69%</li>
                        <li><strong>5:</strong> 70-89%</li>
                        <li><strong>6:</strong> 90-100%</li>
                      </ul>
                    </div>
                  </div>
                )}
                

              </div>
          </>)}


          {activeCalculator === 'bsa' && (<>
              {/* BSA */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-slate-800">BSA (Body Surface Area)</h2>
                  {(() => {
                    const res = calculateBSA();
                    if (!res) return null;
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score} <span className="text-sm text-slate-500 font-normal">m²</span></div>
                        </div>
                        <button onClick={() => handleCopy(`BSA: ${res.score} m²`, 'bsa')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'bsa' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    )
                  })()}
                </div>
                <div className="p-6 space-y-4">
                  <div className={`rounded-lg p-4 mb-4 ${lang === 'ar' ? 'bg-blue-50 border border-blue-200 text-right' : 'bg-blue-50 border border-blue-200 text-left'}`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                    <h3 className="font-bold text-blue-800 mb-2">
                      {lang === 'ar' ? 'تعليمات مساحة الصدفية (قاعدة راحة اليد):' : 'Psoriasis BSA (Rule of Palms):'}
                    </h3>
                    <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                      <li>{lang === 'ar' ? 'راحة يد المريض (بما في ذلك الأصابع) تمثل 1% من مساحة سطح الجسم.' : "1 palm (including fingers) = 1% BSA."}</li>
                      <li>{lang === 'ar' ? 'خفيفة (Mild): أقل من 3%' : 'Mild: < 3%'}</li>
                      <li>{lang === 'ar' ? 'متوسطة (Moderate): من 3% إلى 10%' : 'Moderate: 3 - 10%'}</li>
                      <li>{lang === 'ar' ? 'شديدة (Severe): أكثر من 10%' : 'Severe: > 10%'}</li>
                    </ul>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Weight (kg)</label>
                    <input type="number" value={bmiWeight} onChange={e => setBmiWeight(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition" placeholder="e.g. 70" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Height (cm)</label>
                    <input type="number" value={bmiHeight} onChange={e => setBmiHeight(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition" placeholder="e.g. 175" />
                  </div>
                </div>
              </div>
          </>)}


          {activeCalculator === 'dlqi' && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mt-6 text-center py-12 px-6">
              <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <ExternalLink className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">DLQI (Dermatology Life Quality Index)</h3>
              <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto leading-relaxed">
                {lang === 'ar' 
                  ? 'نظراً لحقوق الملكية الفكرية الخاصة بجامعة كارديف (Cardiff University)، لا يمكن تضمين هذا المقياس مباشرة في النظام. يرجى استخدام الموقع الرسمي لحساب النتيجة.'
                  : 'Due to copyright restrictions by Cardiff University, this calculator cannot be embedded directly. Please use the official website to calculate the score.'}
              </p>
              <a 
                href="https://www.cardiff.ac.uk/medicine/resources/quality-of-life-questionnaires/dermatology-life-quality-index" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg transition"
              >
                {lang === 'ar' ? 'الذهاب إلى حاسبة DLQI الرسمية' : 'Go to Official DLQI Calculator'}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}

          {activeCalculator === 'scorten' && (<>
              {/* SCORTEN */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-slate-800">SCORTEN (TEN Score)</h2>
                  {(() => {
                    const res = calculateSCORTEN();
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-rose-600">{res.score}/7</div>
                          <div className="text-xs text-rose-500 font-bold">Est. Mortality: {res.mortality}</div>
                        </div>
                        <button onClick={() => handleCopy(`SCORTEN: ${res.score}/7 (Est. Mortality: ${res.mortality})`, 'scorten')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'scorten' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    )
                  })()}
                </div>
                <div className="p-6 grid md:grid-cols-2 gap-4">
                  {SCORTEN_PARAMS.map((p, i) => (
                    <label key={i} className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition ${scortenScores[i] ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                      <input type="checkbox" className="w-5 h-5 text-rose-600 rounded" checked={scortenScores[i]} onChange={(e) => {
                        const newScores = [...scortenScores];
                        newScores[i] = e.target.checked;
                        setScortenScores(newScores);
                      }} />
                      <span className="font-medium text-slate-700">{p.label} (+1)</span>
                    </label>
                  ))}
                </div>
              </div>
          </>)}

          {activeCalculator === 'slicc' && (<>
              {/* SLICC */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-slate-800">SLICC 2012 SLE Criteria</h2>
                  {(() => {
                    const res = calculateSLICC();
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className={`text-sm font-black ${res.diagnosis.includes('MET') ? 'text-rose-600' : 'text-slate-500'}`}>{res.diagnosis}</div>
                          <div className="text-xs text-slate-500 font-bold">Clinical: {res.clinical} | Immunologic: {res.immunologic}</div>
                        </div>
                        <button onClick={() => handleCopy(`SLICC SLE Criteria: ${res.diagnosis} (Clinical: ${res.clinical}, Immuno: ${res.immunologic})`, 'slicc')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'slicc' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    )
                  })()}
                </div>
                <div className="p-6">
                  <label className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6 cursor-pointer">
                    <input type="checkbox" className="w-5 h-5 text-amber-600 rounded" checked={sliccLupusNephritis} onChange={(e) => setSliccLupusNephritis(e.target.checked)} />
                    <div>
                      <div className="font-bold text-amber-900">Biopsy-proven Lupus Nephritis</div>
                      <div className="text-xs text-amber-700">If checked, requires only ANA or Anti-dsDNA positivity to meet criteria.</div>
                    </div>
                  </label>

                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="font-bold text-slate-800 mb-3 border-b pb-2">Clinical Criteria ({sliccClinical.filter(Boolean).length})</h3>
                      <div className="space-y-2">
                        {SLICC_CLINICAL.map((item, i) => (
                           <label key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                             <input type="checkbox" className="w-4 h-4 text-indigo-600 rounded" checked={sliccClinical[i]} onChange={(e) => {
                               const arr = [...sliccClinical]; arr[i] = e.target.checked; setSliccClinical(arr);
                             }} />
                             <span className="text-sm text-slate-700">{item}</span>
                           </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800 mb-3 border-b pb-2">Immunologic Criteria ({sliccImmunologic.filter(Boolean).length})</h3>
                      <div className="space-y-2">
                        {SLICC_IMMUNOLOGIC.map((item, i) => (
                           <label key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                             <input type="checkbox" className="w-4 h-4 text-indigo-600 rounded" checked={sliccImmunologic[i]} onChange={(e) => {
                               const arr = [...sliccImmunologic]; arr[i] = e.target.checked; setSliccImmunologic(arr);
                             }} />
                             <span className="text-sm text-slate-700">{item}</span>
                           </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          </>)}

          {activeCalculator === 'pest' && (<>
              {/* PEST Score */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-slate-800">PEST (Psoriatic Arthritis Screening Tool)</h2>
                  {(() => {
                    const res = calculatePEST();
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score}/5</div>
                          <div className={`text-xs font-bold ${res.score >= 3 ? 'text-rose-500' : 'text-emerald-500'}`}>{res.meaning}</div>
                        </div>
                        <button onClick={() => handleCopy(`PEST Score: ${res.score}/5 - ${res.meaning}`, 'pest')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'pest' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    )
                  })()}
                </div>
                <div className="p-6 grid gap-4" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                  {PEST_QUESTIONS[lang].map((q, i) => (
                    <label key={i} className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition ${pestScores[i] ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                      <input type="checkbox" className="w-5 h-5 text-indigo-600 rounded" checked={pestScores[i]} onChange={(e) => {
                        const newScores = [...pestScores];
                        newScores[i] = e.target.checked;
                        setPestScores(newScores);
                      }} />
                      <span className="font-medium text-slate-700">{q} (+1)</span>
                    </label>
                  ))}
                </div>
              </div>
          </>)}

          {activeCalculator === 'frax' && (<>
              {/* FRAX (Link/Placeholder) */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                  <h2 className="text-xl font-bold text-slate-800">FRAX® (Fracture Risk Assessment Tool)</h2>
                </div>
                <div className="p-6 text-center">
                  <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <p className="text-slate-600 mb-6 max-w-lg mx-auto">
                    The FRAX® algorithm is proprietary and requires country-specific epidemiological data. To calculate an accurate 10-year probability of fracture, please use the official tool.
                  </p>
                  <a href="https://frax.shef.ac.uk/FRAX/tool.aspx" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-bold rounded-full hover:bg-indigo-700 transition">
                    Open Official FRAX® Tool <ChevronRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
          </>)}

          {activeCalculator === 'phq9' && (<>
              {/* PHQ-9 Score */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-4 sm:px-6 py-4 border-b border-slate-200 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                  <h2 className="text-lg sm:text-xl font-bold text-slate-800 flex flex-wrap items-center gap-3">
                    <span className="flex items-center gap-2">
                      PHQ-9 (Depression Severity)
                      {!isPatientMode && (
                        <button onClick={() => setActiveGuide('phq9')} className="text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 p-1 rounded-full transition" title={lang === 'ar' ? "الدليل السريري" : "Clinical Guide"}>
                          <Info className="w-5 h-5" />
                        </button>
                      )}
                    </span>
                    {!isPatientMode && (
                      <button onClick={() => handleSendToPatient('phq9')} disabled={!!sharingCalc} className="text-xs font-bold flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full hover:bg-green-100 transition border border-green-200 disabled:opacity-60 disabled:cursor-wait">
                        {sharingCalc === 'phq9' ? (
                          <span className="w-3.5 h-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin inline-block" />
                        ) : (
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                        )}
                        {sharingCalc === 'phq9' ? (lang === 'ar' ? 'جاري الإنشاء...' : 'Generating...') : (lang === 'ar' ? 'إرسال للمريض (واتساب)' : 'Send to Patient')}
                      </button>
                    )}
                  </h2>
                  {(() => {
                    const res = calculatePHQ9();
                    return (
                      <div className="w-full md:w-auto flex justify-between md:justify-end items-center gap-4 text-right">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score}/27</div>
                          <div className="text-xs text-slate-500">{res.severity}</div>
                        </div>
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          <button onClick={() => handleWhatsAppShare(isPatientMode ? `نتيجة استبيان المريض - PHQ-9:\nالنتيجة: ${res.score}/27\nالتصنيف: ${res.severity}` : `PHQ-9 Score: ${res.score}/27 - ${res.severity}`)} className="p-2 text-slate-400 hover:text-green-500 bg-white rounded-lg border border-slate-200" title={isPatientMode ? "إرسال النتيجة للطبيب عبر الواتساب" : "Share via WhatsApp"}>
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                          </button>
                          <button onClick={() => setPhq9Scores(Array(9).fill(0))} className="p-2 text-slate-400 hover:text-red-500 bg-white rounded-lg border border-slate-200" title="Reset">
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleCopy(`PHQ-9 Score: ${res.score}/27 - ${res.severity}`, 'phq9')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                            {copied === 'phq9' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    )
                  })()}
                </div>
                <div className={`px-6 py-3 border-b ${lang === 'ar' ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-200'}`}>
                  <p className={`text-sm font-bold ${lang === 'ar' ? 'text-amber-800' : 'text-slate-700'}`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                    {lang === 'ar' ? 'خلال الأسبوعين الماضيين، كم مرة عانيت من المشاكل التالية؟' : 'Over the last 2 weeks, how often have you been bothered by any of the following problems?'}
                  </p>
                </div>
                <div className="p-6 grid gap-4" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                  {PHQ9_QUESTIONS[lang].map((q, i) => (
                    <div key={i} className="flex flex-col xl:flex-row justify-between xl:items-center gap-4 pb-4 border-b border-slate-100 last:border-0">
                      <div className="text-sm font-medium text-slate-700">
                        {i + 1}. {q}
                      </div>
                      <div className={`flex flex-wrap gap-2 shrink-0 ${lang === 'ar' ? 'justify-end' : ''}`}>
                        {PHQ9_OPTIONS[lang].map((opt, optIndex) => (
                          <button
                            key={PHQ9_OPTIONS['en'][optIndex].value}
                            onClick={() => { const newScores = [...phq9Scores]; newScores[i] = PHQ9_OPTIONS['en'][optIndex].value; setPhq9Scores(newScores); }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${phq9Scores[i] === PHQ9_OPTIONS['en'][optIndex].value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`}
                          >
                            {opt.label} ({PHQ9_OPTIONS['en'][optIndex].value})
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {phq9Scores[8] > 0 && !isPatientMode && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-300 rounded-xl flex items-start gap-3" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                      <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-black text-red-700">
                          {lang === 'ar' ? '⚠️ تحذير سريري — السؤال 9' : '⚠️ Clinical Alert — Question 9'}
                        </p>
                        <p className="text-xs text-red-600 mt-1">
                          {lang === 'ar'
                            ? 'أفاد المريض بوجود أفكار تتعلق بإيذاء النفس. يُوصى بالتقييم السريري الفوري وإجراء محادثة مباشرة مع المريض وفق بروتوكول السلامة المعتمد. (مرجع: APA 2022, PHQ-9 Scoring Guidelines)'
                            : 'Patient endorsed thoughts of self-harm or death (Q9 > 0). Immediate clinical assessment is recommended. Conduct a direct safety evaluation per your institution\'s protocol. (Ref: APA 2022, PHQ-9 Scoring Guidelines)'}
                        </p>
                      </div>
                    </div>
                  )}

                </div>
              </div>
          </>)}

          {activeCalculator === 'bmi' && (<>
              {/* BMI */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-slate-800">BMI Calculator</h2>
                  {(() => {
                    const res = calculateBMI();
                    if (!res) return null;
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score}</div>
                          <div className="text-xs text-slate-500">{res.category}</div>
                        </div>
                        <button onClick={() => handleCopy(`BMI: ${res.score} (${res.category})`, 'bmi')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'bmi' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    )
                  })()}
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Weight (kg)</label>
                    <input type="number" value={bmiWeight} onChange={e => setBmiWeight(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition" placeholder="e.g. 70" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Height (cm)</label>
                    <input type="number" value={bmiHeight} onChange={e => setBmiHeight(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition" placeholder="e.g. 175" />
                  </div>
                </div>
              </div>
          </>)}


          {activeCalculator === 'abcde' && (<>
              {/* ABCDE */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-slate-800">ABCDE (Melanoma Rule)</h2>
                  {(() => {
                    const res = calculateABCDE();
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score}/5</div>
                          <div className={`text-xs font-bold ${res.score >= 1 ? 'text-rose-500' : 'text-emerald-500'}`}>{res.risk}</div>
                        </div>
                        <button onClick={() => handleCopy(`ABCDE Score: ${res.score}/5 - ${res.risk}`, 'abcde')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'abcde' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    )
                  })()}
                </div>
                <div className="p-6 grid gap-4" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                  {ABCDE_PARAMS.map((p, i) => (
                    <label key={i} className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition ${abcdeScores[i] ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                      <input type="checkbox" className="w-5 h-5 text-indigo-600 rounded" checked={abcdeScores[i]} onChange={(e) => {
                        const newScores = [...abcdeScores];
                        newScores[i] = e.target.checked;
                        setAbcdeScores(newScores);
                      }} />
                      <span className="font-medium text-slate-700">{lang === 'ar' ? p.ar : p.label}</span>
                    </label>
                  ))}
                  <p className="text-sm text-slate-500 mt-2">{lang === 'ar' ? '* وجود أي علامة واحدة أو أكثر يستدعي فحصاً دقيقاً أو أخذ خزعة.' : '* The presence of any 1 or more signs warrants close examination or biopsy.'}</p>
                </div>
              </div>
          </>)}

          {activeCalculator === 'acb' && (<>
              {/* ACB Score */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-slate-800">ACB Score (Anticholinergic Burden)</h2>
                  {(() => {
                    const res = calculateACB();
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score}</div>
                          <div className={`text-xs font-bold ${res.score >= 3 ? 'text-rose-500' : 'text-emerald-500'}`}>{res.meaning}</div>
                        </div>
                        <button onClick={() => handleCopy(`ACB Score: ${res.score} - ${res.meaning}`, 'acb')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'acb' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    )
                  })()}
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Level 1 Drugs (Mild Effect)</label>
                    <input type="number" min="0" value={acbLevel1} onChange={e => setAcbLevel1(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 transition" placeholder="Number of Level 1 drugs..." />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Level 2 Drugs (Moderate Effect)</label>
                    <input type="number" min="0" value={acbLevel2} onChange={e => setAcbLevel2(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 transition" placeholder="Number of Level 2 drugs..." />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Level 3 Drugs (Severe Effect)</label>
                    <input type="number" min="0" value={acbLevel3} onChange={e => setAcbLevel3(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 transition" placeholder="Number of Level 3 drugs..." />
                  </div>
                  
                  <div className="pt-4 border-t border-slate-100">
                    <button onClick={() => setShowAcbDrugs(!showAcbDrugs)} className="text-indigo-600 font-bold text-sm hover:underline flex items-center gap-1">
                      {showAcbDrugs ? (lang === 'ar' ? 'إخفاء قائمة الأدوية' : 'Hide Drug List') : (lang === 'ar' ? 'عرض قائمة الأدوية المسببة الكاملة' : 'View Full Drug List')}
                    </button>
                    {showAcbDrugs && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4" dir="ltr">
                        <div className="bg-slate-50 border border-slate-200 rounded p-3">
                          <h4 className="font-bold text-slate-700 mb-2 border-b pb-1">Level 3 (Score: 3)</h4>
                          <p className="text-xs text-slate-600 leading-relaxed">{ACB_DRUGS.level3.join(', ')}</p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded p-3">
                          <h4 className="font-bold text-slate-700 mb-2 border-b pb-1">Level 2 (Score: 2)</h4>
                          <p className="text-xs text-slate-600 leading-relaxed">{ACB_DRUGS.level2.join(', ')}</p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded p-3">
                          <h4 className="font-bold text-slate-700 mb-2 border-b pb-1">Level 1 (Score: 1)</h4>
                          <p className="text-xs text-slate-600 leading-relaxed">{ACB_DRUGS.level1.join(', ')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-4">* Enter the number of medications the patient is currently taking in each category. A score of 3 or more indicates a high risk of cognitive impairment.</p>
                </div>
              </div>
          </>)}

          {activeCalculator === 'hads' && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mt-6 text-center py-12 px-6">
              <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <ExternalLink className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Hospital Anxiety and Depression Scale (HADS)</h3>
              <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto leading-relaxed">
                {lang === 'ar' 
                  ? 'نظراً لحقوق الملكية الفكرية الخاصة بمؤسسة (GL Assessment)، يمنع تضمين هذا المقياس رقمياً بدون ترخيص تجاري. يرجى استخدام الروابط الخارجية المعتمدة.'
                  : 'Due to copyright restrictions by GL Assessment, this scale cannot be embedded digitally without a commercial license. Please use official external links.'}
              </p>
              <a 
                href="https://www.svri.org/sites/default/files/attachments/2016-01-13/HADS.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg transition"
              >
                {lang === 'ar' ? 'عرض مقياس HADS الخارجي' : 'View External HADS Scale'}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}

          {activeCalculator === 'gad7' && (<>
              {/* GAD-7 Score */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-4 sm:px-6 py-4 border-b border-slate-200 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                  <h2 className="text-lg sm:text-xl font-bold text-slate-800 flex flex-wrap items-center gap-3">
                    <span className="flex items-center gap-2">
                      GAD-7 (General Anxiety Disorder)
                      {!isPatientMode && (
                        <button onClick={() => setActiveGuide('gad7')} className="text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 p-1 rounded-full transition" title={lang === 'ar' ? "الدليل السريري" : "Clinical Guide"}>
                          <Info className="w-5 h-5" />
                        </button>
                      )}
                    </span>
                    {!isPatientMode && (
                      <button onClick={() => handleSendToPatient('gad7')} disabled={!!sharingCalc} className="text-xs font-bold flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full hover:bg-green-100 transition border border-green-200 disabled:opacity-60 disabled:cursor-wait">
                        {sharingCalc === 'gad7' ? (
                          <span className="w-3.5 h-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin inline-block" />
                        ) : (
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                        )}
                        {sharingCalc === 'gad7' ? (lang === 'ar' ? 'جاري الإنشاء...' : 'Generating...') : (lang === 'ar' ? 'إرسال للمريض (واتساب)' : 'Send to Patient')}
                      </button>
                    )}
                  </h2>
                  {(() => {
                    const res = calculateGAD7();
                    return (
                      <div className="w-full md:w-auto flex justify-between md:justify-end items-center gap-4 text-right">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score}/21</div>
                          <div className="text-xs text-slate-500">{res.severity}</div>
                        </div>
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          <button onClick={() => handleWhatsAppShare(isPatientMode ? `نتيجة استبيان المريض - GAD-7:\nالنتيجة: ${res.score}/21\nالتصنيف: ${res.severity}` : `GAD-7 Score: ${res.score}/21 - ${res.severity}`)} className="p-2 text-slate-400 hover:text-green-500 bg-white rounded-lg border border-slate-200" title={isPatientMode ? "إرسال النتيجة للطبيب عبر الواتساب" : "Share via WhatsApp"}>
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                          </button>
                          <button onClick={() => setGad7Scores(Array(7).fill(0))} className="p-2 text-slate-400 hover:text-red-500 bg-white rounded-lg border border-slate-200" title="Reset">
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleCopy(`GAD-7 Score: ${res.score}/21 - ${res.severity}`, 'gad7')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                            {copied === 'gad7' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    )
                  })()}
                </div>
                <div className={`px-6 py-3 border-b ${lang === 'ar' ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-200'}`}>
                  <p className={`text-sm font-bold ${lang === 'ar' ? 'text-amber-800' : 'text-slate-700'}`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                    {lang === 'ar' ? 'خلال الأسبوعين الماضيين، كم مرة عانيت من المشاكل التالية؟' : 'Over the last 2 weeks, how often have you been bothered by the following problems?'}
                  </p>
                </div>
                <div className="p-6 grid gap-4" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                  {GAD7_QUESTIONS[lang].map((q, i) => (
                    <div key={i} className="flex flex-col xl:flex-row justify-between xl:items-center gap-4 pb-4 border-b border-slate-100 last:border-0">
                      <div className="text-sm font-medium text-slate-700">
                        {i + 1}. {q}
                      </div>
                      <div className={`flex flex-wrap gap-2 shrink-0 ${lang === 'ar' ? 'justify-end' : ''}`}>
                        {PHQ9_OPTIONS[lang].map((opt, optIndex) => (
                          <button
                            key={PHQ9_OPTIONS['en'][optIndex].value}
                            onClick={() => { const newScores = [...gad7Scores]; newScores[i] = PHQ9_OPTIONS['en'][optIndex].value; setGad7Scores(newScores); }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${gad7Scores[i] === PHQ9_OPTIONS['en'][optIndex].value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`}
                          >
                            {opt.label} ({PHQ9_OPTIONS['en'][optIndex].value})
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
          </>)}

          {activeCalculator === 'easi' && (<>
              {/* EASI Score */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 flex-wrap">
                    EASI (Eczema Area and Severity Index)
                    <button onClick={() => setActiveGuide('easi')} className="text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 p-1 rounded-full transition" title={lang === 'ar' ? "الدليل السريري" : "Clinical Guide"}>
                      <Info className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => setShowEasiGuide(true)}
                      className="ml-4 flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 hover:bg-blue-200 hover:text-blue-800 rounded-full text-xs font-bold transition-colors"
                      title="Show User Guide & Atlas"
                    >
                      <BookOpen className="w-4 h-4" />
                      Show Guide & Atlas
                    </button>
                  </h2>
                </div>

                {(() => {
                  const headMult = easiIsChild ? 0.2 : 0.1;
                  const upperMult = 0.2;
                  const trunkMult = 0.3;
                  const lowerMult = easiIsChild ? 0.3 : 0.4;
                  
                  const headRes = getEasiRegionDetails(easiHead, headMult);
                  const upperRes = getEasiRegionDetails(easiUpper, upperMult);
                  const trunkRes = getEasiRegionDetails(easiTrunk, trunkMult);
                  const lowerRes = getEasiRegionDetails(easiLower, lowerMult);
                  const totalEasi = headRes.total + upperRes.total + trunkRes.total + lowerRes.total;

                  return (
                    <div className="p-6">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 border-b border-slate-100 pb-6">
                        <div className="text-3xl font-bold text-slate-500 flex items-center gap-3">
                           <div>EASI SCORE: <span className="text-orange-600">{totalEasi.toFixed(1)}</span></div>
                           <button onClick={() => handleCopy(`EASI Score: ${totalEasi.toFixed(1)}`, 'easi')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                            {copied === 'easi' ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                          </button>
                        </div>
                        
                        <div className="flex items-center gap-3 bg-slate-50 p-2 rounded-xl border border-slate-200">
                          <span className="text-sm font-semibold text-slate-600 px-2">Patient Age:</span>
                          <button
                            onClick={() => setEasiIsChild(false)}
                            className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-colors ${!easiIsChild ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-100'}`}
                          >
                            ≥ 8 Years
                          </button>
                          <button
                            onClick={() => setEasiIsChild(true)}
                            className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-colors ${easiIsChild ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-100'}`}
                          >
                            &lt; 8 Years
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        {[
                          { id: 'head', title: `Head and Neck (${easiIsChild ? '20%' : '10%'})`, state: easiHead, setter: setEasiHead, res: headRes },
                          { id: 'upper', title: 'Upper Limbs (20%)', state: easiUpper, setter: setEasiUpper, res: upperRes },
                          { id: 'trunk', title: 'Trunk (30%)', state: easiTrunk, setter: setEasiTrunk, res: trunkRes },
                          { id: 'lower', title: `Lower Limbs (${easiIsChild ? '30%' : '40%'})`, state: easiLower, setter: setEasiLower, res: lowerRes },
                        ].map((region) => (
                          <div key={region.id} className="border border-slate-300 rounded bg-slate-50 overflow-hidden flex flex-col">
                            <div className="bg-slate-300 px-3 py-2 font-bold text-slate-800">{region.title}</div>
                            <div className="flex bg-white h-full">
                               <div className="w-full">
                                 <table className="w-full text-sm">
                                   <tbody>
                                     <tr className="border-b border-slate-100 bg-slate-50">
                                       <td className="p-2 text-blue-500 font-bold text-xs uppercase w-1/2">Area Score (0-6)</td>
                                       <td className="p-2 font-bold flex items-center gap-2">
                                         <select 
                                            className="border border-slate-300 rounded p-1 bg-white outline-none w-full"
                                            value={region.state.area}
                                            onChange={(e) => region.setter({ ...region.state, area: parseInt(e.target.value) })}
                                          >
                                            <option value={0}>0 (0%)</option>
                                            <option value={1}>1 (1-9%)</option>
                                            <option value={2}>2 (10-29%)</option>
                                            <option value={3}>3 (30-49%)</option>
                                            <option value={4}>4 (50-69%)</option>
                                            <option value={5}>5 (70-89%)</option>
                                            <option value={6}>6 (90-100%)</option>
                                          </select>
                                       </td>
                                     </tr>
                                     {[
                                       { key: 'e', label: 'Erythema (Redness)' },
                                       { key: 'i', label: 'Induration/Papulation' },
                                       { key: 'ex', label: 'Excoriation (Scratching)' },
                                       { key: 'l', label: 'Lichenification' },
                                     ].map(grade => (
                                       <tr key={grade.key} className="border-b border-slate-100">
                                         <td className="p-2 text-slate-600 font-semibold text-[11px] uppercase">{grade.label}</td>
                                         <td className="p-2 font-bold">
                                            <select 
                                              className="border border-slate-200 rounded p-1 bg-white outline-none w-full"
                                              value={(region.state as any)[grade.key]}
                                              onChange={(e) => region.setter({ ...region.state, [grade.key]: parseInt(e.target.value) })}
                                            >
                                              {[0,1,2,3].map(v => <option key={v} value={v}>{v}</option>)}
                                            </select>
                                         </td>
                                       </tr>
                                     ))}
                                     <tr className="border-t-2 border-slate-200 bg-slate-50">
                                       <td className="p-2 font-black text-slate-800 text-xs">REGION TOTAL:</td>
                                       <td className="p-2 font-black text-slate-800">{region.res.total.toFixed(1)}</td>
                                     </tr>
                                   </tbody>
                                 </table>
                               </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
          </>)}

      {activeCalculator === 'salt' && (() => {
        const v = Number(saltVertex) || 0;
        const rp = Number(saltRightProfile) || 0;
        const lp = Number(saltLeftProfile) || 0;
        const p = Number(saltPosterior) || 0;
        const totalScore = (v * 0.40) + (rp * 0.18) + (lp * 0.18) + (p * 0.24);
        
        let subtype = 'S0';
        let color = 'text-green-600 bg-green-50 border-green-200';
        if (totalScore > 0 && totalScore < 25) { subtype = 'S1'; color = 'text-blue-600 bg-blue-50 border-blue-200'; }
        else if (totalScore >= 25 && totalScore < 50) { subtype = 'S2'; color = 'text-yellow-600 bg-yellow-50 border-yellow-200'; }
        else if (totalScore >= 50 && totalScore < 75) { subtype = 'S3'; color = 'text-orange-600 bg-orange-50 border-orange-200'; }
        else if (totalScore >= 75 && totalScore < 100) { subtype = 'S4'; color = 'text-red-600 bg-red-50 border-red-200'; }
        else if (totalScore >= 100) { subtype = 'S5'; color = 'text-red-800 bg-red-100 border-red-300'; }

        return (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center flex-wrap gap-4">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 flex-wrap">
                SALT (Severity of Alopecia Tool)
                <button onClick={() => setActiveGuide('salt')} className="text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 p-1 rounded-full transition" title={lang === 'ar' ? "الدليل السريري" : "Clinical Guide"}>
                  <Info className="w-5 h-5" />
                </button>
              </h2>
              <div className={`px-4 py-2 rounded-xl border ${color} flex items-center gap-3`}>
                <div className="text-right">
                  <div className="text-xs font-bold uppercase tracking-wider opacity-80">{lang === 'ar' ? 'درجة SALT' : 'SALT Score'}</div>
                  <div className="text-2xl font-black">{totalScore.toFixed(1)}</div>
                </div>
                <div className="h-8 w-px bg-current opacity-20"></div>
                <div className="text-xl font-black">{subtype}</div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Vertex */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    {lang === 'ar' ? 'أعلى الرأس (Vertex) - 40%' : 'Vertex - 40%'}
                  </label>
                  <div className="flex items-center gap-3">
                    <input 
                      type="number" min="0" max="100" 
                      value={saltVertex} 
                      onChange={e => setSaltVertex(e.target.value === '' ? '' : Math.min(100, Math.max(0, Number(e.target.value))))}
                      className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-slate-800 font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                      placeholder="0-100"
                    />
                    <span className="text-slate-500 font-bold">%</span>
                  </div>
                </div>
                {/* Right Profile */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    {lang === 'ar' ? 'الجانب الأيمن (Right Profile) - 18%' : 'Right Profile - 18%'}
                  </label>
                  <div className="flex items-center gap-3">
                    <input 
                      type="number" min="0" max="100" 
                      value={saltRightProfile} 
                      onChange={e => setSaltRightProfile(e.target.value === '' ? '' : Math.min(100, Math.max(0, Number(e.target.value))))}
                      className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-slate-800 font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                      placeholder="0-100"
                    />
                    <span className="text-slate-500 font-bold">%</span>
                  </div>
                </div>
                {/* Left Profile */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    {lang === 'ar' ? 'الجانب الأيسر (Left Profile) - 18%' : 'Left Profile - 18%'}
                  </label>
                  <div className="flex items-center gap-3">
                    <input 
                      type="number" min="0" max="100" 
                      value={saltLeftProfile} 
                      onChange={e => setSaltLeftProfile(e.target.value === '' ? '' : Math.min(100, Math.max(0, Number(e.target.value))))}
                      className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-slate-800 font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                      placeholder="0-100"
                    />
                    <span className="text-slate-500 font-bold">%</span>
                  </div>
                </div>
                {/* Posterior */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    {lang === 'ar' ? 'الخلف (Posterior) - 24%' : 'Posterior - 24%'}
                  </label>
                  <div className="flex items-center gap-3">
                    <input 
                      type="number" min="0" max="100" 
                      value={saltPosterior} 
                      onChange={e => setSaltPosterior(e.target.value === '' ? '' : Math.min(100, Math.max(0, Number(e.target.value))))}
                      className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-slate-800 font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                      placeholder="0-100"
                    />
                    <span className="text-slate-500 font-bold">%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

          {activeCalculator === 'uas7' && (<>
              {/* UAS7 Score */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-slate-800">UAS7 (Urticaria Activity Score over 7 days)</h2>
                  {(() => {
                    const res = calculateUAS7();
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score}/42</div>
                          <div className="text-xs text-slate-500">{res.severity}</div>
                        </div>
                        <button onClick={() => handleCopy(`UAS7 Score: ${res.score}/42 - ${res.severity}`, 'uas7')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'uas7' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    )
                  })()}
                </div>
                <div className="p-6">
                   <p className="text-sm text-slate-600 mb-6">Enter the patient's daily scores for the past 7 days. Wheals (0=None, 1=&lt;20, 2=20-50, 3=&gt;50). Pruritus (0=None, 1=Mild, 2=Moderate, 3=Severe).</p>
                   <div className="overflow-x-auto">
                     <table className="w-full min-w-[600px] text-sm text-left">
                       <thead className="bg-slate-100 text-slate-700">
                         <tr>
                           <th className="p-3 rounded-tl-lg">Day</th>
                           <th className="p-3 text-center">Wheals (0-3)</th>
                           <th className="p-3 text-center">Pruritus (0-3)</th>
                           <th className="p-3 text-center rounded-tr-lg">Daily Total</th>
                         </tr>
                       </thead>
                       <tbody>
                         {[0,1,2,3,4,5,6].map(day => (
                           <tr key={day} className="border-b border-slate-100 hover:bg-slate-50">
                             <td className="p-3 font-bold text-slate-700">Day {day + 1}</td>
                             <td className="p-3 text-center">
                               <select className="border border-slate-300 rounded p-1" value={uas7Wheals[day]} onChange={e => { const arr=[...uas7Wheals]; arr[day]=parseInt(e.target.value); setUas7Wheals(arr); }}>
                                 {[0,1,2,3].map(v => <option key={v} value={v}>{v}</option>)}
                               </select>
                             </td>
                             <td className="p-3 text-center">
                               <select className="border border-slate-300 rounded p-1" value={uas7Itch[day]} onChange={e => { const arr=[...uas7Itch]; arr[day]=parseInt(e.target.value); setUas7Itch(arr); }}>
                                 {[0,1,2,3].map(v => <option key={v} value={v}>{v}</option>)}
                               </select>
                             </td>
                             <td className="p-3 text-center font-bold text-slate-800">{uas7Wheals[day] + uas7Itch[day]}</td>
                           </tr>
                         ))}
                       </tbody>
                     </table>
                   </div>
                </div>
              </div>
          </>)}


          {activeCalculator === 'cvrisk' && (<>
              {/* CV Risk */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-slate-800">10-Year ASCVD Risk (like PREVENT)</h2>
                    {(() => {
                      const res = calculateCVRisk();
                      if (!res) return <div className="text-sm font-medium text-slate-400">Enter valid values to calculate</div>;
                      return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score}%</div>
                          <div className={`text-xs font-bold ${parseFloat(res.score) >= 7.5 ? 'text-rose-500' : 'text-emerald-500'}`}>{res.risk}</div>
                        </div>
                        <button onClick={() => handleCopy(`10-Year ASCVD Risk: ${res.score}% - ${res.risk}`, 'cvrisk')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'cvrisk' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    )
                  })()}
                </div>
                <div className="p-6 grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Age (20-79 years)</label>
                    <input type="number" step="any" value={cvAge} onChange={e => setCvAge(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 transition" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Sex</label>
                    <select value={cvSex} onChange={e => setCvSex(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 transition bg-white">
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Race</label>
                    <select value={cvRace} onChange={e => setCvRace(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 transition bg-white">
                      <option value="white">White / Other</option>
                      <option value="aa">African American</option>
                    </select>
                  </div>
                  <div>
                    <label className="flex justify-between block text-sm font-bold text-slate-700 mb-1">
                      <span>Total Cholesterol</span>
                      <select value={cvUnit} onChange={e => setCvUnit(e.target.value)} className="text-indigo-600 bg-transparent border-none outline-none cursor-pointer text-xs font-bold p-0">
                        <option value="mg/dl">mg/dL</option>
                        <option value="mmol/l">mmol/L</option>
                      </select>
                    </label>
                    <input type="number" step="any" value={cvTc} onChange={e => setCvTc(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 transition" />
                  </div>
                  <div>
                    <label className="flex justify-between block text-sm font-bold text-slate-700 mb-1">
                      <span>HDL Cholesterol</span>
                      <select value={cvUnit} onChange={e => setCvUnit(e.target.value)} className="text-indigo-600 bg-transparent border-none outline-none cursor-pointer text-xs font-bold p-0">
                        <option value="mg/dl">mg/dL</option>
                        <option value="mmol/l">mmol/L</option>
                      </select>
                    </label>
                    <input type="number" step="any" value={cvHdl} onChange={e => setCvHdl(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 transition" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Systolic BP (mmHg)</label>
                    <input type="number" step="any" value={cvSbp} onChange={e => setCvSbp(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 transition" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Receiving BP Treatment?</label>
                    <select value={cvTreated} onChange={e => setCvTreated(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 transition bg-white">
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Diabetes History?</label>
                    <select value={cvDiabetes} onChange={e => setCvDiabetes(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 transition bg-white">
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Current Smoker?</label>
                    <select value={cvSmoker} onChange={e => setCvSmoker(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500 transition bg-white">
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                </div>
              </div>
          </>)}

          {activeCalculator === 'egfr' && (<>
              {/* eGFR Score */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-slate-800">eGFR (CKD-EPI 2021)</h2>
                  {(() => {
                    const res = calculateEGFR();
                    if (!res) return null;
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score} <span className="text-sm font-normal text-slate-500">mL/min/1.73m²</span></div>
                          <div className="text-xs text-slate-500">{res.stage}</div>
                        </div>
                        <button onClick={() => handleCopy(`eGFR: ${res.score} mL/min/1.73m² (${res.stage})`, 'egfr')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'egfr' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    )
                  })()}
                </div>
                <div className="p-6 grid gap-6 md:grid-cols-3">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Age (Years)</label>
                    <input type="number" value={egfrAge} onChange={e => setEgfrAge(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500" placeholder="e.g. 60" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Sex</label>
                    <div className="flex gap-2">
                       <button onClick={() => setEgfrSex('Male')} className={`flex-1 p-3 rounded-lg border font-bold text-sm ${egfrSex === 'Male' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-300'}`}>Male</button>
                       <button onClick={() => setEgfrSex('Female')} className={`flex-1 p-3 rounded-lg border font-bold text-sm ${egfrSex === 'Female' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-300'}`}>Female</button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Serum Creatinine (mg/dL)</label>
                    <input type="number" step="0.01" value={egfrCr} onChange={e => setEgfrCr(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-indigo-500" placeholder="e.g. 1.1" />
                  </div>
                </div>
              </div>
          </>)}

          {activeCalculator === 'cha2ds2vasc' && (<>
              {/* CHA2DS2-VASc */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-slate-800">CHA2DS2-VASc (AFib Stroke Risk)</h2>
                  {(() => {
                    const res = calculateCHA2DS2VASC();
                    return (
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <div className="text-2xl font-black text-indigo-600">{res.score}</div>
                          <div className={`text-xs font-bold ${res.score >= 2 ? 'text-rose-500' : 'text-emerald-500'}`}>{res.risk}</div>
                        </div>
                        <button onClick={() => handleCopy(`CHA2DS2-VASc Score: ${res.score} (${res.risk})`, 'cha2ds2vasc')} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'cha2ds2vasc' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    )
                  })()}
                </div>
                <div className="p-6 grid gap-4">
                  {CHA2DS2VASC_PARAMS.map((p, i) => (
                    <label key={i} className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition ${cha2ds2vascScores[i] ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                      <input type="checkbox" className="w-5 h-5 text-indigo-600 rounded" checked={cha2ds2vascScores[i]} onChange={(e) => {
                        const newScores = [...cha2ds2vascScores];
                        newScores[i] = e.target.checked;
                        setCha2ds2vascScores(newScores);
                      }} />
                      <span className="font-medium text-slate-700">{p.label} <strong className="text-indigo-600 ml-1">(+{p.points})</strong></span>
                    </label>
                  ))}
                </div>
              </div>
          </>)}

          {activeCalculator === 'act' && (() => {
            const totalScore = actScores.reduce((a, b) => a + b, 0);
            const isComplete = actScores.every(s => s > 0);
            return (
                              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 sm:px-6 py-4 border-b border-slate-200 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                    <h2 className="text-lg sm:text-xl font-bold text-slate-800 flex flex-wrap items-center gap-3">
                      <span className="flex items-center gap-2">
                        Asthma Control Test (ACT)
                      </span>
                      {!isPatientMode && (
                        <button onClick={() => handleSendToPatient('act')} disabled={!!sharingCalc} className="text-xs font-bold flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full hover:bg-green-100 transition border border-green-200 disabled:opacity-60 disabled:cursor-wait">
                          {sharingCalc === 'act' ? (
                            <span className="w-3.5 h-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin inline-block" />
                          ) : (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                          )}
                          {sharingCalc === 'act' ? (lang === 'ar' ? 'جاري التحضير...' : 'Generating...') : (lang === 'ar' ? 'إرسال للمريض (واتساب)' : 'Send to Patient')}
                        </button>
                      )}
                    </h2>
                    <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto">
                      <div className="text-right">
                        <div className="text-2xl font-black text-indigo-700">{isComplete ? totalScore : '-/25'}</div>
                        <div className="text-xs text-slate-500 font-bold">{isComplete ? 'Total Score' : 'Answer all'}</div>
                      </div>
                      <div className="flex items-center space-x-2 rtl:space-x-reverse border-l border-slate-300 pl-4 rtl:pl-0 rtl:border-l-0 rtl:border-r rtl:pr-4">
                        <button onClick={() => {
                          const resMeaning = totalScore <= 19 ? 'May not be well controlled' : 'Well controlled';
                          handleWhatsAppShare(isPatientMode ? `نتيجة حاسبة الربو - ACT:\nالنتيجة: ${totalScore}/25\nالتقييم: ${resMeaning}` : `ACT Score: ${totalScore}/25 - ${resMeaning}`);
                        }} className="p-2 text-slate-400 hover:text-green-500 bg-white rounded-lg border border-slate-200" title={isPatientMode ? "شارك النتيجة للطبيب عبر الواتساب" : "Share via WhatsApp"}>
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                        </button>
                        <button onClick={() => setActScores(Array(5).fill(0))} className="p-2 text-slate-400 hover:text-red-500 bg-white rounded-lg border border-slate-200" title="Reset">
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button onClick={() => {
                          const resMeaning = totalScore <= 19 ? 'May not be well controlled' : 'Well controlled';
                          handleCopy(`ACT Score: ${totalScore}/25 - ${resMeaning}`, 'act');
                        }} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'act' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6 bg-white">
                {isComplete && totalScore <= 19 && !isPatientMode && (
                  <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex items-start gap-3"><AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" /><div><p className="text-sm font-bold text-red-800">{lang === 'en' ? 'Alert' : 'تنبيه'}</p><p className="text-sm text-red-700">{lang === 'en' ? 'A score of 19 or less suggests that your asthma may not be well controlled.' : 'درجة 19 أو أقل تشير إلى أن الربو قد لا يكون مسيطراً عليه بشكل جيد.'}</p></div></div>
                )}
                {isComplete && totalScore > 19 && !isPatientMode && (
                  <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg flex items-start gap-3"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><div><p className="text-sm font-bold text-green-800">{lang === 'en' ? 'Well Controlled' : 'مسيطر عليه جيداً'}</p><p className="text-sm text-green-700">{lang === 'en' ? 'A score of 20 or higher suggests that asthma is well controlled.' : 'درجة 20 أو أعلى تشير إلى أن الربو مسيطر عليه بشكل جيد.'}</p></div></div>
                )}
                <div className="space-y-6">
                  {ACT_QUESTIONS[lang].map((q, idx) => (
                    <div key={idx} className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <p className="font-bold text-slate-800 mb-4">{idx + 1}. {q}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                        {ACT_OPTIONS[lang][idx].map((opt, optIdx) => {
                          const score = optIdx + 1; // All questions are ordered from worst (1) to best (5)
                          return (
                            <button key={optIdx} onClick={() => { const newS = [...actScores]; newS[idx] = score; setActScores(newS); }} className={`flex flex-col items-center justify-center p-3 sm:p-4 gap-1.5 rounded-xl border transition-all duration-200 ${actScores[idx] === score ? 'bg-indigo-600 text-white border-indigo-600 shadow-md ring-2 ring-indigo-600 ring-offset-1' : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 hover:shadow-sm'}`}>
                              <span className={`text-[11px] font-bold ${actScores[idx] === score ? 'text-indigo-200' : 'text-slate-400'}`}>
                                {lang === 'ar' ? `النقاط: ${score}` : `Score: ${score}`}
                              </span>
                              <span className="text-sm font-bold text-center leading-tight">
                                {opt}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            );
          })()}
          {activeCalculator === 'cat' && (() => {
            const totalScore = catScores.reduce((a, b) => a + (b === -1 ? 0 : b), 0);
            const isComplete = catScores.every(s => s !== -1);
            return (
                              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 sm:px-6 py-4 border-b border-slate-200 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                    <h2 className="text-lg sm:text-xl font-bold text-slate-800 flex flex-wrap items-center gap-3">
                      <span className="flex items-center gap-2">
                        COPD Assessment Test (CAT)
                      </span>
                      {!isPatientMode && (
                        <button onClick={() => handleSendToPatient('cat')} disabled={!!sharingCalc} className="text-xs font-bold flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full hover:bg-green-100 transition border border-green-200 disabled:opacity-60 disabled:cursor-wait">
                          {sharingCalc === 'cat' ? (
                            <span className="w-3.5 h-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin inline-block" />
                          ) : (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                          )}
                          {sharingCalc === 'cat' ? (lang === 'ar' ? 'جاري التحضير...' : 'Generating...') : (lang === 'ar' ? 'إرسال للمريض (واتساب)' : 'Send to Patient')}
                        </button>
                      )}
                    </h2>
                    <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto">
                      <div className="text-right">
                        <div className="text-2xl font-black text-indigo-700">{isComplete ? totalScore : '-/40'}</div>
                        <div className="text-xs text-slate-500 font-bold">{isComplete ? 'Total Score' : 'Answer all'}</div>
                      </div>
                      <div className="flex items-center space-x-2 rtl:space-x-reverse border-l border-slate-300 pl-4 rtl:pl-0 rtl:border-l-0 rtl:border-r rtl:pr-4">
                        <button onClick={() => {
                          const resMeaning = totalScore < 10 ? 'Low impact' : totalScore <= 20 ? 'Medium impact' : totalScore <= 30 ? 'High impact' : 'Very high impact';
                          handleWhatsAppShare(isPatientMode ? `نتيجة حاسبة مرض الانسداد الرئوي - CAT:\nالنتيجة: ${totalScore}/40\nالتأثير: ${resMeaning}` : `CAT Score: ${totalScore}/40 - ${resMeaning}`);
                        }} className="p-2 text-slate-400 hover:text-green-500 bg-white rounded-lg border border-slate-200" title={isPatientMode ? "شارك النتيجة للطبيب عبر الواتساب" : "Share via WhatsApp"}>
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                        </button>
                        <button onClick={() => setCatScores(Array(8).fill(-1))} className="p-2 text-slate-400 hover:text-red-500 bg-white rounded-lg border border-slate-200" title="Reset">
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button onClick={() => {
                          const resMeaning = totalScore < 10 ? 'Low impact' : totalScore <= 20 ? 'Medium impact' : totalScore <= 30 ? 'High impact' : 'Very high impact';
                          handleCopy(`CAT Score: ${totalScore}/40 - ${resMeaning}`, 'cat');
                        }} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                          {copied === 'cat' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6 bg-white">
                {isComplete && totalScore >= 21 && !isPatientMode && (
                  <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex items-start gap-3"><AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" /><div><p className="text-sm font-bold text-red-800">{lang === 'en' ? 'High Impact' : 'تأثير شديد'}</p><p className="text-sm text-red-700">{lang === 'en' ? 'High or very high impact of COPD on patient life.' : 'تأثير شديد أو شديد جداً لمرض الانسداد الرئوي على حياة المريض.'}</p></div></div>
                )}
                <div className="space-y-6">
                  {CAT_QUESTIONS[lang].map((qPair, idx) => {
                    const [left, right] = qPair.split(' / ');
                    return (
                      <div key={idx} className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <div className="flex justify-between text-sm font-bold text-slate-700 mb-3"><span>{left}</span><span className="text-right">{right}</span></div>
                        <div className="flex justify-between items-center gap-1 sm:gap-2">
                          {[0, 1, 2, 3, 4, 5].map(score => (
                            <button key={score} onClick={() => { const newS = [...catScores]; newS[idx] = score; setCatScores(newS); }} className={`flex-1 py-3 text-lg font-black rounded-lg border transition ${catScores[idx] === score ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-400 border-slate-300 hover:border-indigo-400'}`}>
                              {score}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            );
          })()}
          {activeCalculator === 'puma' && (() => {
            const calculatePuma = () => {
              let score = 0;
              if (pumaState.gender === 'male') score += 1;
              if (pumaState.age === '50-59') score += 1;
              if (pumaState.age === '>=60') score += 2;
              if (pumaState.packYears === '20-30') score += 1;
              if (pumaState.packYears === '>30') score += 2;
              if (pumaState.dyspnea === 'yes') score += 1;
              if (pumaState.sputum === 'yes') score += 1;
              if (pumaState.cough === 'yes') score += 1;
              if (pumaState.spirometry === 'yes') score += 1;
              return score;
            };
            const totalScore = calculatePuma();
            const isComplete = Object.values(pumaState).every(v => v !== '');
            return (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-4 sm:px-6 py-4 border-b border-slate-200 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                  <h2 className="text-lg sm:text-xl font-bold text-slate-800 flex flex-wrap items-center gap-3">
                    <span className="flex items-center gap-2">
                      PUMA Score (COPD Risk)
                    </span>
                    {!isPatientMode && (
                      <button onClick={() => handleSendToPatient('puma')} disabled={!!sharingCalc} className="text-xs font-bold flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full hover:bg-green-100 transition border border-green-200 disabled:opacity-60 disabled:cursor-wait">
                        {sharingCalc === 'puma' ? (
                          <span className="w-3.5 h-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin inline-block" />
                        ) : (
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                        )}
                        {sharingCalc === 'puma' ? (lang === 'ar' ? 'جاري التحضير...' : 'Generating...') : (lang === 'ar' ? 'إرسال للمريض (واتساب)' : 'Send to Patient')}
                      </button>
                    )}
                  </h2>
                  <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto">
                    <div className="text-right">
                      <div className="text-2xl font-black text-indigo-700">{isComplete ? totalScore : '-/9'}</div>
                      <div className="text-xs text-slate-500 font-bold">{isComplete ? 'Total Score' : 'Answer all'}</div>
                    </div>
                    <div className="flex items-center space-x-2 rtl:space-x-reverse border-l border-slate-300 pl-4 rtl:pl-0 rtl:border-l-0 rtl:border-r rtl:pr-4">
                      <button onClick={() => {
                        const resMeaning = totalScore < 7 ? 'Low risk of COPD' : 'High risk of COPD';
                        handleWhatsAppShare(isPatientMode ? `نتيجة حاسبة بوما - PUMA:\nالنتيجة: ${totalScore}/9\nالخطر: ${resMeaning}` : `PUMA Score: ${totalScore}/9 - ${resMeaning}`);
                      }} className="p-2 text-slate-400 hover:text-green-500 bg-white rounded-lg border border-slate-200" title={isPatientMode ? "شارك النتيجة للطبيب عبر الواتساب" : "Share via WhatsApp"}>
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                      </button>
                      <button onClick={() => setPumaState({ gender: '', age: '', packYears: '', dyspnea: '', sputum: '', cough: '', spirometry: '' })} className="p-2 text-slate-400 hover:text-red-500 bg-white rounded-lg border border-slate-200" title="Reset">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button onClick={() => {
                        const resMeaning = totalScore < 7 ? 'Low risk of COPD' : 'High risk of COPD';
                        handleCopy(`PUMA Score: ${totalScore}/9 - ${resMeaning}`, 'puma');
                      }} className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200">
                        {copied === 'puma' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-4 sm:p-6 bg-white">
                {isComplete && totalScore >= 5 && !isPatientMode && (
                  <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex items-start gap-3"><AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" /><div><p className="text-sm font-bold text-red-800">{lang === 'en' ? 'High Risk' : 'خطر عالي'}</p><p className="text-sm text-red-700">{lang === 'en' ? 'Score ≥ 5. Spirometry test is highly recommended to confirm COPD.' : 'الدرجة ≥ 5. يوصى بشدة بإجراء فحص قياس التنفس لتأكيد تشخيص COPD.'}</p></div></div>
                )}
                {isComplete && totalScore < 5 && (
                  <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg flex items-start gap-3"><CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /><div><p className="text-sm font-bold text-green-800">{lang === 'en' ? 'Low Risk' : 'خطر منخفض'}</p><p className="text-sm text-green-700">{lang === 'en' ? 'Score < 5. Low risk for COPD.' : 'الدرجة < 5. خطر منخفض للإصابة بـ COPD.'}</p></div></div>
                )}
                <div className="space-y-4">
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex justify-between items-center">
                    <p className="font-bold text-slate-800">{PUMA_QUESTIONS[lang].gender}</p>
                    <div className="flex gap-2"><button onClick={() => setPumaState({...pumaState, gender: 'male'})} className={`px-4 py-2 text-sm font-bold rounded-lg border transition ${pumaState.gender === 'male' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-indigo-50'}`}>{lang === 'en' ? 'Male' : 'ذكر'}</button><button onClick={() => setPumaState({...pumaState, gender: 'female'})} className={`px-4 py-2 text-sm font-bold rounded-lg border transition ${pumaState.gender === 'female' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-indigo-50'}`}>{lang === 'en' ? 'Female' : 'أنثى'}</button></div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <p className="font-bold text-slate-800 mb-3">{PUMA_QUESTIONS[lang].age}</p>
                    <div className="flex gap-2"><button onClick={() => setPumaState({...pumaState, age: '<40'})} className={`flex-1 py-2 text-sm font-bold rounded-lg border transition ${pumaState.age === '<40' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-indigo-50'}`}>{'< 40'}</button><button onClick={() => setPumaState({...pumaState, age: '40-49'})} className={`flex-1 py-2 text-sm font-bold rounded-lg border transition ${pumaState.age === '40-49' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-indigo-50'}`}>40 - 49</button><button onClick={() => setPumaState({...pumaState, age: '50-59'})} className={`flex-1 py-2 text-sm font-bold rounded-lg border transition ${pumaState.age === '50-59' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-indigo-50'}`}>50 - 59</button><button onClick={() => setPumaState({...pumaState, age: '>=60'})} className={`flex-1 py-2 text-sm font-bold rounded-lg border transition ${pumaState.age === '>=60' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-indigo-50'}`}>{'≥ 60'}</button></div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <p className="font-bold text-slate-800 mb-3">{PUMA_QUESTIONS[lang].packYears}</p>
                    <div className="flex gap-2"><button onClick={() => setPumaState({...pumaState, packYears: '<20'})} className={`flex-1 py-2 text-sm font-bold rounded-lg border transition ${pumaState.packYears === '<20' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-indigo-50'}`}>{'< 20'}</button><button onClick={() => setPumaState({...pumaState, packYears: '20-30'})} className={`flex-1 py-2 text-sm font-bold rounded-lg border transition ${pumaState.packYears === '20-30' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-indigo-50'}`}>20 - 30</button><button onClick={() => setPumaState({...pumaState, packYears: '>30'})} className={`flex-1 py-2 text-sm font-bold rounded-lg border transition ${pumaState.packYears === '>30' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-indigo-50'}`}>{'> 30'}</button></div>
                  </div>
                  {['dyspnea', 'sputum', 'cough', 'spirometry'].map(field => (
                    <div key={field} className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex justify-between items-center gap-4">
                      <p className="font-bold text-slate-800 flex-1">{(PUMA_QUESTIONS[lang] as any)[field]}</p>
                      <div className="flex gap-2"><button onClick={() => setPumaState({...pumaState, [field]: 'yes'})} className={`px-4 py-2 text-sm font-bold rounded-lg border transition ${(pumaState as any)[field] === 'yes' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-indigo-50'}`}>{lang === 'en' ? 'Yes' : 'نعم'}</button><button onClick={() => setPumaState({...pumaState, [field]: 'no'})} className={`px-4 py-2 text-sm font-bold rounded-lg border transition ${(pumaState as any)[field] === 'no' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-indigo-50'}`}>{lang === 'en' ? 'No' : 'لا'}</button></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            );
          })()}
        </div>

        {!isPatientMode && (
          <footer className="mt-12 pt-6 border-t border-slate-200 text-center print:hidden mb-8">
            <p className="text-sm font-bold text-slate-500">
              Dr.Watheq A. Hashim Al-Chalabi
            </p>
          </footer>
        )}
        {isPatientMode && (
          <div className="mt-10 mx-4 p-5 bg-amber-50 border border-amber-200 rounded-2xl text-center print:hidden mb-8">
            <p className="text-xs font-bold text-amber-700 mb-1">
              ⚠️ إخلاء المسؤولية / Disclaimer
            </p>
            <p className="text-xs text-amber-600 leading-relaxed" dir="rtl">
              هذا الاستبيان أداة مساعدة سريرية فقط ولا يغني عن تقييم طبي متخصص. جميع النتائج تخضع لتقدير طبيبك المختص.
            </p>
            <p className="text-xs text-amber-500 mt-1" dir="ltr">
              This questionnaire is a clinical aid only and does not replace a specialist evaluation. Results are subject to your physician’s assessment.
            </p>
          </div>
        )}
      </div>
      {/* Render Modals if any */}
      {showEasiGuide && (
        <EasiGuideModal onClose={() => setShowEasiGuide(false)} />
      )}
      {showPasiGuide && (
        <PasiGuideModal onClose={() => setShowPasiGuide(false)} />
      )}
      {activePasiInfo && (
        <PasiInfoModal type={activePasiInfo} onClose={() => setActivePasiInfo(null)} />
      )}
      <CalculatorGuideModal 
        isOpen={activeGuide !== null} 
        onClose={() => setActiveGuide(null)} 
        calculatorType={activeGuide || 'dlqi'} 
        lang={lang} 
      />
    </div>
  );
}